import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { notificar } from '../utils/notify';
import CartaDesligamento from '../components/CartaDesligamento';
import EmailRescisaoColaborador from '../components/EmailRescisaoColaborador';
import EmailPreviaRescisao from '../components/EmailPreviaRescisao';
import ContratoAnexo from '../components/ContratoAnexo';
import FinalizacaoManual from '../components/FinalizacaoManual';
import VinculoBadge from '../components/VinculoBadge';
import PrazoBadge from '../components/PrazoBadge';
import { contratoExigido } from '../utils/contrato';
import { copiarParaClipboard } from '../utils/credenciaisBpoRh';
import ComplementoGuiaExame from '../components/ComplementoGuiaExame';
import '../styles/bporh_admissoes.css';

// Página de Rescisões do BPO RH — espelho da Convenia (tabelas convenia_*).
// Fluxo: empresas com desligamentos → colaboradores desligados → detalhe completo.
// O formulário público traz o nome digitado — este badge diz se o colaborador
// existe mesmo no espelho da Convenia daquela empresa (checado pelo backend)
const BadgeVerificacao = ({ s }) => {
  const cc = s.colaborador_convenia;
  switch (s.verificacao) {
    case 'encontrado':
      return (
        <span className="badge" title={`Encontrado na Convenia: ${cc?.nome}${cc?.cargo ? ` · ${cc.cargo}` : ''}`}
          style={{ backgroundColor: '#e6f7ef', color: '#116b47', border: '1.5px solid #1fa971' }}>
          <i className="fas fa-user-check me-1"></i>na Convenia
        </span>
      );
    case 'ja_desligado':
      return (
        <span className="badge" title={`Já consta como desligado${cc?.data_desligamento ? ` em ${cc.data_desligamento}` : ''} — solicitação possivelmente duplicada`}
          style={{ backgroundColor: '#fff5da', color: '#8a6d00', border: '1.5px solid #f1be48' }}>
          <i className="fas fa-user-clock me-1"></i>já desligado{cc?.data_desligamento ? ` ${cc.data_desligamento}` : ''}
        </span>
      );
    case 'ambiguo':
      return (
        <span className="badge" title="Mais de um colaborador da empresa casa com esse nome — confira antes de processar"
          style={{ backgroundColor: '#f1e8ff', color: '#5b21b6', border: '1.5px solid #8b5cf6' }}>
          <i className="fas fa-people-arrows me-1"></i>nome ambíguo
        </span>
      );
    case 'sem_cliente':
      return (
        <span className="badge" title="O CNPJ do formulário não casou com nenhum cliente da carteira"
          style={{ backgroundColor: '#fdeaec', color: '#a11d2b', border: '1.5px solid #dc3545' }}>
          <i className="fas fa-building-circle-xmark me-1"></i>empresa não identificada
        </span>
      );
    default:
      return (
        <span className="badge" title="Nenhum colaborador da empresa casa com esse nome no espelho da Convenia — confira a grafia antes de processar"
          style={{ backgroundColor: '#fdeaec', color: '#a11d2b', border: '1.5px solid #dc3545' }}>
          <i className="fas fa-user-xmark me-1"></i>não encontrado
        </span>
      );
  }
};

const BpoRhRescisoes = () => {
  const [empresas, setEmpresas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [mensagem, setMensagem] = useState('');
  // Lista de empresas abre com o histórico completo; ao abrir uma empresa o
  // foco é a operação corrente (30 dias) — mesmo comportamento das Admissões
  const DIAS_EMPRESAS = 36500;
  const DIAS_EMPRESA_ABERTA = 30;
  const [dias, setDias] = useState(DIAS_EMPRESAS);

  useEffect(() => { notificar(mensagem); }, [mensagem]);

  const [empresaSelecionada, setEmpresaSelecionada] = useState(null);
  // A empresa aberta vive na URL (?cliente=) — o voltar do navegador fecha a
  // empresa em vez de pular para outra rota
  const [searchParams, setSearchParams] = useSearchParams();
  const [pedirFinalizacao, setPedirFinalizacao] = useState(null);     // convenia_id a abrir no "solicitar"
  const abrirEmpresa = (emp) => setSearchParams({ cliente: String(emp.cliente_id) });
  // Atalho da lista de pendências: abre o checklist do desligamento direto.
  // 'aberto=1' mantém o histórico completo — pendências antigas ficariam fora
  // da janela de 30 dias que a empresa assume por padrão.
  const abrirDesligamentoAberto = (d) => {
    setAbaDetalhe('checklist');
    setColaboradorDetalhe(d.colaborador);
    setSearchParams({ cliente: String(d.cliente_id), aberto: '1' });
  };
  const fecharEmpresa = () => setSearchParams({});
  const [colaboradorDetalhe, setColaboradorDetalhe] = useState(null);
  const [busca, setBusca] = useState('');
  const [filtroOperador, setFiltroOperador] = useState('');   // visão de empresas
  const [filtroCargo, setFiltroCargo] = useState('');          // visão de colaboradores
  const [filtroDepto, setFiltroDepto] = useState('');
  // status do checklist e situação do prazo — filtros separados porque
  // respondem a perguntas diferentes: 'o que falta fazer' x 'o que vai vencer'
  const [filtroStatus, setFiltroStatus] = useState('');
  const [filtroPrazo, setFiltroPrazo] = useState('');
  // Período por data de desligamento (AAAA-MM-DD dos inputs type=date)
  const [filtroDe, setFiltroDe] = useState('');
  const [filtroAte, setFiltroAte] = useState('');
  const periodoAtivo = Boolean(filtroDe || filtroAte);

  // O intervalo filtra o que o backend já devolveu — usar as datas força a
  // janela para 'Todo o período' para o filtro alcançar tudo (igual Admissões)
  useEffect(() => {
    if (periodoAtivo && dias !== 36500) setDias(36500);
  }, [periodoAtivo, dias]);

  // dd/mm/aaaa -> número ordenável (sem data vai para o fim)
  const dataOrdenavel = (d) => {
    if (!d) return 0;
    const p = String(d).split('/');
    return p.length === 3 ? parseInt(p[2] + p[1] + p[0], 10) : 0;
  };
  const isoOrdenavel = (d) => (d ? parseInt(d.replaceAll('-', ''), 10) : 0);
  const [mostrarCompleto, setMostrarCompleto] = useState(false);
  useEffect(() => { setMostrarCompleto(false); }, [colaboradorDetalhe]);

  // ─── Checklist de rescisão do colaborador (bporh_rescisao_tarefas) ───
  const [abaDetalhe, setAbaDetalhe] = useState('checklist');
  const [checklist, setChecklist] = useState(null);
  const [carregandoCheck, setCarregandoCheck] = useState(false);
  const [obsEdit, setObsEdit] = useState('');
  const [salvandoObs, setSalvandoObs] = useState(false);
  const [marcando, setMarcando] = useState(null);        // campo em atualização

  const paramsChecklist = useCallback(() => ({
    cliente_id: empresaSelecionada?.cliente_id,
    convenia_id: colaboradorDetalhe?.convenia_id,
    colaborador: [colaboradorDetalhe?.nome, colaboradorDetalhe?.sobrenome].filter(Boolean).join(' '),
  }), [empresaSelecionada, colaboradorDetalhe]);

  // 'Concluído' e 'Não se aplica' fecham a atividade — mesma regra do backend
  const SEM_ACENTO = new RegExp('[\\u0300-\\u036f]', 'g');
  const atividadeFechada = (v) => {
    const n = (v || '').normalize('NFD').replace(SEM_ACENTO, '').trim().toLowerCase();
    return n.startsWith('conclu') || n.startsWith('nao se aplica');
  };

  // Reflete o checklist do modal nos badges da tabela e nos cards sem recarregar
  // a lista. Vive num estado à parte porque mexer em `empresas` trocaria a
  // identidade da empresa aberta — o efeito da URL a re-selecionaria e o GET do
  // checklist rodaria de novo em loop.
  const [chkOverride, setChkOverride] = useState({});   // convenia_id -> resumo

  const sincronizarBadges = useCallback((tarefas) => {
    const conveniaId = colaboradorDetalhe?.convenia_id;
    if (!conveniaId || !tarefas?.length) return;
    const pendentes = tarefas.filter(t => !atividadeFechada(t.valor));
    setChkOverride((o) => ({
      ...o,
      [conveniaId]: {
        total: tarefas.length,
        pendentes: pendentes.length,
        concluidas: tarefas.length - pendentes.length,
        proxima: pendentes.length ? pendentes[0].rotulo : null,
        finalizado_em: pendentes.length ? null : new Date().toLocaleDateString('pt-BR'),
      },
    }));
  }, [colaboradorDetalhe]);   // eslint-disable-line react-hooks/exhaustive-deps

  // resumo do checklist de um colaborador: o override do modal vence o do servidor
  const resumoChk = useCallback(
    (c) => (c && chkOverride[c.convenia_id]) || c?.checklist || {},
    [chkOverride]);

  useEffect(() => {
    if (!colaboradorDetalhe || !empresaSelecionada) { setChecklist(null); return; }
    setCarregandoCheck(true);
    const accessToken = localStorage.getItem('access');
    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/bporh/rescisao-checklist/`, {
        params: paramsChecklist(),
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then((res) => {
        setChecklist(res.data);
        setObsEdit(res.data.obs || '');
        // 'novo' = ainda não existe checklist gravado, o GET só mostrou a prévia
        // do modelo. A lista já exibe esse modelo como 0/N 'Aguardando execução';
        // só sincroniza o badge quando há registro — abrir para consultar não
        // inicia processo.
        if (!res.data.novo) sincronizarBadges(res.data.tarefas);
      })
      .catch(() => setChecklist(null))
      .finally(() => setCarregandoCheck(false));
  }, [colaboradorDetalhe, empresaSelecionada, paramsChecklist, sincronizarBadges]);


  const marcarCampo = (campo, valor) => {
    setMarcando(campo);
    // atualização otimista, no modal e nos badges da tabela/cards
    const tarefas = (checklist?.tarefas || []).map(
      t => t.campo === campo ? { ...t, valor } : t);
    // esta marcação é o que faz o checklist existir no banco — deixa de ser prévia
    setChecklist((c) => c && ({ ...c, tarefas, novo: false }));
    sincronizarBadges(tarefas);
    const accessToken = localStorage.getItem('access');
    axios
      .patch(`${process.env.REACT_APP_API_BASE}/api/bporh/rescisao-checklist/`,
        { ...paramsChecklist(), campo, valor },
        { headers: { Authorization: `Bearer ${accessToken}` } })
      .catch(() => setMensagem('Não foi possível marcar a tarefa'))
      .finally(() => setMarcando(null));
  };

  // ─── tarefa → categoria de serviço (bporh_servico): botão "Abrir <sistema>"
  // com o portal cadastrado e a chave de login/senha, como nas Admissões
  const CAMPO_CATEGORIA_R = {
    ex_demissional: 'Medicina do Trabalho', ponto: 'Ponto', vt: 'Vale Transporte',
    va_vr: 'Benefícios', farmacia: 'Benefícios', academia: 'Academia',
    odonto: 'Plano de Saúde e Odonto', pl_saude: 'Plano de Saúde e Odonto',
    assinatura: 'Assinador Digital', contabilidade: 'Comunicação Contabilidade',
  };
  const portaisDaTarefa = (campo) => {
    const cat = CAMPO_CATEGORIA_R[campo];
    if (!cat) return [];
    let lista = empresaSelecionada?.portais_categoria?.[cat] || [];
    // 'Benefícios' guarda o vale E os convênios de farmácia: cada tarefa pega o seu
    if (campo === 'va_vr') lista = lista.filter(x => !/farm[aá]cia|alura|secury|wellhub/i.test(x.propriedade));
    if (campo === 'farmacia') lista = lista.filter(x => /farm[aá]cia/i.test(x.propriedade));
    return lista;
  };
  const [credPortal, setCredPortal] = useState({});   // {servicoId: {aberto, senha, carregando}}
  const alternarCredPortal = (pt) => {
    const atual = credPortal[pt.id] || {};
    if (atual.aberto) { setCredPortal(c => ({ ...c, [pt.id]: { ...atual, aberto: false } })); return; }
    if (atual.senha != null || !pt.tem_senha) { setCredPortal(c => ({ ...c, [pt.id]: { ...atual, aberto: true } })); return; }
    setCredPortal(c => ({ ...c, [pt.id]: { ...atual, aberto: true, carregando: true } }));
    axios.get(`${process.env.REACT_APP_API_BASE}/api/bporh-credencial/`,
      { params: { tipo: 'servico', id: pt.id, campo: 'senha' },
        headers: { Authorization: `Bearer ${localStorage.getItem('access')}` } })
      .then((res) => setCredPortal(c => ({ ...c, [pt.id]: { aberto: true, senha: res.data.valor || '', carregando: false } })))
      .catch(() => { setMensagem('Não foi possível obter a senha'); setCredPortal(c => ({ ...c, [pt.id]: { aberto: false } })); });
  };
  const copiarCred = (texto, rotulo) => {
    copiarParaClipboard(texto).then(() => setMensagem(`${rotulo} copiado`)).catch(() => setMensagem('Não foi possível copiar'));
  };

  // ─── Tarefas VT / VA / Home Office: quanto foi pago ao colaborador e quanto
  // caiu depois do desligamento (dias pagos e não trabalhados) → desconto na
  // rescisão. Um modal só; o endpoint muda por benefício, a saída é a mesma.
  const BENEF_RESC = {
    vt: { campo: 'vt', titulo: 'VT / Auxílio Mobilidade', icone: 'fa-bus', botao: 'Conferir VT pago',
      url: (cid) => `${process.env.REACT_APP_API_BASE}/api/bpo-vt/${cid}/rescisao/`, params: {} },
    va: { campo: 'va_vr', titulo: 'Vale Alimentação', icone: 'fa-utensils', botao: 'Conferir VA pago',
      url: (cid) => `${process.env.REACT_APP_API_BASE}/api/bpo-beneficios/${cid}/rescisao/`, params: { tipo: 'va' } },
    home_office: { campo: 'home_office', titulo: 'Auxílio Home Office', icone: 'fa-house-laptop', botao: 'Conferir Home Office pago',
      url: (cid) => `${process.env.REACT_APP_API_BASE}/api/bpo-beneficios/${cid}/rescisao/`, params: { tipo: 'home_office' } },
  };
  const [benefResc, setBenefResc] = useState(null);   // {tipo, carregando, dados, erro, anotando}
  const abrirBenefResc = (colab, tipo) => {
    const cfg = BENEF_RESC[tipo];
    const cliente_id = empresaSelecionada?.cliente_id || colab.cliente_id;
    setBenefResc({ tipo, carregando: true });
    axios.get(cfg.url(cliente_id), {
      params: { convenia_id: colab.convenia_id, ...cfg.params },
      headers: { Authorization: `Bearer ${localStorage.getItem('access')}` },
    })
      .then((res) => setBenefResc({ tipo, carregando: false, dados: res.data }))
      .catch((err) => setBenefResc({ tipo, carregando: false, erro: err.response?.data?.mensagem || `Não foi possível conferir o ${cfg.titulo}` }));
  };
  // grava o resumo como observação da tarefa (mesmo PATCH da observação manual)
  const anotarBenefNaTarefa = () => {
    if (!benefResc?.dados) return;
    const campo = BENEF_RESC[benefResc.tipo].campo;
    const texto = (benefResc.dados.resumo || '').slice(0, 500);
    setBenefResc(v => ({ ...v, anotando: true }));
    axios.patch(`${process.env.REACT_APP_API_BASE}/api/bporh/rescisao-checklist/`,
      { ...paramsChecklist(), campo, obs_tarefa: texto },
      { headers: { Authorization: `Bearer ${localStorage.getItem('access')}` } })
      .then(() => {
        setChecklist((c) => c && ({ ...c, tarefas: c.tarefas.map(t => t.campo === campo ? { ...t, obs: texto } : t) }));
        setBenefResc(null);
        setMensagem(`Conferência anotada na tarefa ${BENEF_RESC[benefResc.tipo].titulo}`);
      })
      .catch(() => setBenefResc(v => ({ ...v, anotando: false, erro: 'Não foi possível anotar na tarefa' })));
  };
  const brlR = (v) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

  // ─── Tarefa Ponto: desligar o colaborador no sistema de ponto (caminho
  // inverso da admissão). GET = prévia (quem foi achado lá, situação, o que
  // muda); POST = executa. A tarefa vira 'Concluído' quando dá certo.
  const sistemasPontoApi = (empresaSelecionada?.sistemas_ponto || []).filter(x => x.api);
  const [desligPonto, setDesligPonto] = useState(null);   // {colab, sistema, carregando, previa, erro, data, executando}

  // ─── Desligar na Convenia pelo checklist (prévia + confirmação; irreversível) ───
  const [desligConv, setDesligConv] = useState(null);     // {colab, carregando, dados, form, erro, executando}
  const abrirDesligarConvenia = (colab) => {
    const cliente_id = empresaSelecionada?.cliente_id || colab.cliente_id;
    setDesligConv({ colab, cliente_id, carregando: true, dados: null, form: null, erro: null, executando: false });
    axios.post(`${process.env.REACT_APP_API_BASE}/api/bporh/convenia/desligar/`,
      { cliente_id, convenia_id: colab.convenia_id, dry_run: true },
      { headers: { Authorization: `Bearer ${localStorage.getItem('access')}` } })
      .then((res) => {
        const d = res.data; const sg = d.sugestao || {};
        setDesligConv((s) => ({ ...s, carregando: false, dados: d,
          form: { tipo_desligamento: '', aviso_previo: '', data_final_desligamento: sg.data_final_desligamento || '',
            data_inicio_aviso: sg.data_inicio_aviso || '', data_remocao_acesso: sg.data_remocao_acesso || '',
            desvincular_beneficios: 'Sim', quebra_contrato: false, motivo_desligamento: '' } }));
      })
      .catch((err) => setDesligConv((s) => ({ ...s, carregando: false,
        erro: err.response?.data?.mensagem || 'Não foi possível consultar a Convenia' })));
  };
  const confirmarDesligarConvenia = () => {
    if (!desligConv?.dados) return;
    // já desligado na Convenia: confirmar só marca a tarefa
    if (desligConv.dados.ja_desligado) {
      marcarCampo('convenia', 'Concluído'); setDesligConv(null); return;
    }
    setDesligConv((s) => ({ ...s, executando: true, erro: null }));
    axios.post(`${process.env.REACT_APP_API_BASE}/api/bporh/convenia/desligar/`,
      { cliente_id: desligConv.cliente_id, convenia_id: desligConv.colab.convenia_id, confirmar: true, ajustes: desligConv.form },
      { headers: { Authorization: `Bearer ${localStorage.getItem('access')}` } })
      .then((res) => {
        setDesligConv(null);
        notificar(res.data.mensagem || 'Desligamento criado na Convenia');
        const t = (checklist?.tarefas || []).find(x => x.campo === 'convenia');
        if (t && !(t.valor || '').toLowerCase().startsWith('conclu')) marcarCampo('convenia', 'Concluído');
        carregar();
      })
      .catch((err) => setDesligConv((s) => ({ ...s, executando: false,
        erro: err.response?.data?.mensagem || 'Não foi possível desligar na Convenia' })));
  };
  const carregarPreviaDesligar = (colab, sistema) => {
    const cliente_id = empresaSelecionada?.cliente_id || colab.cliente_id;
    setDesligPonto(d => ({ ...(d || {}), colab, sistema, carregando: true, previa: null, erro: null }));
    axios.get(`${process.env.REACT_APP_API_BASE}/api/bpo-ponto/${cliente_id}/desligar/`, {
      params: { convenia_id: colab.convenia_id, sistema },
      headers: { Authorization: `Bearer ${localStorage.getItem('access')}` },
    })
      .then((res) => setDesligPonto(d => ({ ...d, carregando: false, previa: res.data, sistema: res.data.sistema,
        data: res.data.data_sugerida || '' })))
      .catch((err) => setDesligPonto(d => ({ ...d, carregando: false,
        erro: err.response?.data?.mensagem || err.response?.data?.erro || 'Não foi possível montar a prévia',
        naoAchado: err.response?.status === 404 ? err.response?.data : null })));
  };
  const abrirDesligarPonto = (colab) => {
    if (!sistemasPontoApi.length) return;
    carregarPreviaDesligar(colab, sistemasPontoApi[0].tipo);
  };
  const confirmarDesligarPonto = () => {
    if (!desligPonto?.previa) return;
    const cliente_id = empresaSelecionada?.cliente_id || desligPonto.colab.cliente_id;
    setDesligPonto(d => ({ ...d, executando: true }));
    axios.post(`${process.env.REACT_APP_API_BASE}/api/bpo-ponto/${cliente_id}/desligar/`, {
      convenia_id: desligPonto.colab.convenia_id, sistema: desligPonto.sistema,
      data_desligamento: desligPonto.data || null,
    }, { headers: { Authorization: `Bearer ${localStorage.getItem('access')}` } })
      .then((res) => {
        setDesligPonto(null);
        notificar(res.data.mensagem || 'Desligado do ponto');
        const t = (checklist?.tarefas || []).find(x => x.campo === 'ponto');
        if (t && !(t.valor || '').toLowerCase().startsWith('conclu')) marcarCampo('ponto', 'Concluído');
      })
      .catch((err) => setDesligPonto(d => ({ ...d, executando: false,
        erro: err.response?.data?.erro || err.response?.data?.mensagem || 'Não foi possível desligar' })));
  };

  // ─── Aviso/Carta: pedido da carta de desligamento (só em pedido do empregado) ───
  const [cartaDesl, setCartaDesl] = useState(null);     // {cliente_id, convenia_id, nome}
  const concluirCartaDesl = (mensagemOk) => {
    const t = (checklist?.tarefas || []).find(x => x.campo === 'aviso_carta');
    setCartaDesl(null);
    setMensagem(mensagemOk);
    if (t && !(t.valor || '').toLowerCase().startsWith('conclu')) marcarCampo('aviso_carta', 'Solicitado');
  };

  // ─── Assinatura: e-mail ao colaborador com os documentos da rescisão (PDFs conferidos) ───
  const [emailColab, setEmailColab] = useState(null);   // {cliente_id, convenia_id, nome}
  const concluirEmailColab = (mensagemOk) => {
    const t = (checklist?.tarefas || []).find(x => x.campo === 'assinatura');
    setEmailColab(null);
    setMensagem(mensagemOk);
    if (t && !(t.valor || '').toLowerCase().startsWith('conclu')) marcarCampo('assinatura', 'Solicitado');
  };

  // ─── Prévia da rescisão: e-mail ao cliente antes do Financeiro (GaussFleet, 21/10/2026) ───
  const [emailPrevia, setEmailPrevia] = useState(null);   // {cliente_id, convenia_id, nome}
  const concluirEmailPrevia = (mensagemOk) => {
    setEmailPrevia(null);
    setMensagem(mensagemOk);
    marcarCampo('previa_rescisao', 'Concluído');
  };

  // ─── Exame Demissional: pedido à clínica com a guia em PDF (fluxo do Exame Mensal) ───
  const [exameDem, setExameDem] = useState(null);
  const [exameDemCarregando, setExameDemCarregando] = useState(false);
  const [exameDemEnviando, setExameDemEnviando] = useState(false);
  const abrirExameDem = (colab) => {
    const cliente_id = empresaSelecionada?.cliente_id || colab.cliente_id;
    setExameDemCarregando(true);
    axios.get(`${process.env.REACT_APP_API_BASE}/api/bporh/exames/solicitar/`, {
      params: { cliente_id, convenia_id: colab.convenia_id, tipo: 'demissional' },
      headers: { Authorization: `Bearer ${localStorage.getItem('access')}` },
    })
      .then((res) => {
        const d = res.data;
        setExameDem({
          colab, cliente_id, remetentes: d.remetentes || [], config: d.config || {},
          operador_id: (d.remetentes || [])[0]?.id || '', data: '',
          assunto: d.email?.assunto || '', corpo: d.email?.corpo || '',
          historico: d.historico || [], destinatario: d.config?.clinica_email || '', destSalvando: false,
          faltando: d.faltando || [],
        });
      })
      .catch((err) => setMensagem(err.response?.data?.mensagem || 'Não foi possível montar a solicitação do exame'))
      .finally(() => setExameDemCarregando(false));
  };
  // 'Visualizar e-mail': o e-mail montado (moldura Comece, de/para/cc) + a guia PDF,
  // exatamente como vai sair — todo envio passa por prévia
  const [emailPreviewDem, setEmailPreviewDem] = useState(null);
  const visualizarEmailDem = () => {
    if (!exameDem) return;
    setEmailPreviewDem({ carregando: true });
    const cab = { headers: { Authorization: `Bearer ${localStorage.getItem('access')}` } };
    Promise.all([
      axios.post(`${process.env.REACT_APP_API_BASE}/api/bporh/exames/solicitar/`, {
        cliente_id: Number(exameDem.cliente_id), convenia_id: exameDem.colab.convenia_id, visualizar: true,
        tipo_exame: 'demissional', data_exame: exameDem.data || null, operador_id: exameDem.operador_id || null,
        assunto: exameDem.assunto, corpo: exameDem.corpo,
      }, cab),
      axios.get(`${process.env.REACT_APP_API_BASE}/api/bporh/exames/solicitar/`, {
        params: { cliente_id: exameDem.cliente_id, convenia_id: exameDem.colab.convenia_id, pdf: 1,
                  tipo: 'demissional', data_exame: exameDem.data || '' },
        ...cab, responseType: 'blob' }),
    ])
      .then(([email, pdf]) => setEmailPreviewDem({
        ...email.data, carregando: false,
        pdfUrl: URL.createObjectURL(new Blob([pdf.data], { type: 'application/pdf' })),
      }))
      .catch((err) => {
        setEmailPreviewDem(null);
        setMensagem(err.response?.data?.mensagem || 'Não foi possível montar a prévia do e-mail');
      });
  };
  const fecharPreviewDem = () => {
    if (emailPreviewDem?.pdfUrl) URL.revokeObjectURL(emailPreviewDem.pdfUrl);
    setEmailPreviewDem(null);
  };
  const verGuiaDem = () => {
    if (!exameDem) return;
    axios.get(`${process.env.REACT_APP_API_BASE}/api/bporh/exames/solicitar/`, {
      params: { cliente_id: exameDem.cliente_id, convenia_id: exameDem.colab.convenia_id, pdf: 1,
                tipo: 'demissional', data_exame: exameDem.data || '' },
      headers: { Authorization: `Bearer ${localStorage.getItem('access')}` }, responseType: 'blob',
    })
      .then((res) => window.open(URL.createObjectURL(res.data), '_blank'))
      .catch(() => setMensagem('Não foi possível gerar a guia'));
  };
  const salvarDestinatarioDem = () => {
    if (!exameDem) return;
    const email = (exameDem.destinatario || '').trim();
    if (!email) { setMensagem('Informe o e-mail da clínica'); return; }
    setExameDem(x => ({ ...x, destSalvando: true }));
    axios.post(`${process.env.REACT_APP_API_BASE}/api/bporh/exames/solicitar/`,
      { cliente_id: Number(exameDem.cliente_id), config: { ...exameDem.config, clinica_email: email } },
      { headers: { Authorization: `Bearer ${localStorage.getItem('access')}` } })
      .then((res) => setExameDem(x => ({ ...x, config: res.data.config, destinatario: res.data.config?.clinica_email || email, destSalvando: false })))
      .catch((err) => { setMensagem(err.response?.data?.mensagem || 'Não foi possível salvar o destinatário'); setExameDem(x => ({ ...x, destSalvando: false })); });
  };
  // depois do pedido (por e-mail ou pelo portal), a tarefa vira 'Solicitado'
  const concluirPedidoExameDem = (mensagemOk) => {
    const t = (checklist?.tarefas || []).find(x => x.campo === 'ex_demissional');
    setExameDem(null);
    setEmailPreviewDem(null);
    setMensagem(mensagemOk);
    if (t && !(t.valor || '').toLowerCase().startsWith('conclu')) marcarCampo('ex_demissional', 'Solicitado');
  };
  const enviarExameDem = () => {
    if (!exameDem) return;
    if (!exameDem.operador_id) { setMensagem('Escolha o operador que assina o envio'); return; }
    if (!exameDem.config?.clinica_email) { setMensagem('Informe e salve o e-mail da clínica antes de enviar'); return; }
    setExameDemEnviando(true);
    axios.post(`${process.env.REACT_APP_API_BASE}/api/bporh/exames/solicitar/`, {
      cliente_id: Number(exameDem.cliente_id), convenia_ids: [exameDem.colab.convenia_id],
      operador_id: exameDem.operador_id, tipo_exame: 'demissional', data_exame: exameDem.data || null,
      assunto: exameDem.assunto, corpo: exameDem.corpo,
    }, { headers: { Authorization: `Bearer ${localStorage.getItem('access')}` } })
      .then((res) => concluirPedidoExameDem(res.data.mensagem || 'Solicitação enviada à clínica'))
      .catch((err) => setMensagem(err.response?.data?.mensagem || 'Não foi possível enviar'))
      .finally(() => setExameDemEnviando(false));
  };
  const registrarExamePortalDem = () => {
    if (!exameDem) return;
    setExameDemEnviando(true);
    axios.post(`${process.env.REACT_APP_API_BASE}/api/bporh/exames/solicitar/`, {
      cliente_id: Number(exameDem.cliente_id), convenia_ids: [exameDem.colab.convenia_id],
      tipo_exame: 'demissional', data_exame: exameDem.data || null, via: 'portal',
    }, { headers: { Authorization: `Bearer ${localStorage.getItem('access')}` } })
      .then((res) => concluirPedidoExameDem(res.data.mensagem || 'Pedido registrado'))
      .catch((err) => setMensagem(err.response?.data?.mensagem || 'Não foi possível registrar'))
      .finally(() => setExameDemEnviando(false));
  };

  // obs POR TAREFA do checklist de rescisão
  const [obsTarefaEdit, setObsTarefaEdit] = useState(null);   // {campo, texto}
  // há PDF do contrato anexado? enquanto não houver, o botão "Concluído" da
  // tarefa "Contrato assinado" fica travado (o ContratoAnexo avisa por onEstado)
  const [temContrato, setTemContrato] = useState(false);
  // PJ, sócio, estagiário e autônomo não têm contrato de emprego: o botão
  // 'Concluído' não pode ficar travado esperando um PDF que não existe
  const [exigeContrato, setExigeContrato] = useState(true);
  // ao trocar de colaborador o anexo é outro — não herdar o estado do anterior
  useEffect(() => { setTemContrato(false); }, [colaboradorDetalhe]);

  // Exclusão da rescisão (só do banco do Saber — na Convenia é manual).
  // Desfaz o desligamento do espelho: o colaborador volta a constar como ativo.
  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false);
  const [excluindoRescisao, setExcluindoRescisao] = useState(false);
  useEffect(() => { setConfirmandoExclusao(false); }, [colaboradorDetalhe]);

  const excluirRescisao = () => {
    if (!colaboradorDetalhe || !empresaSelecionada) return;
    setExcluindoRescisao(true);
    const accessToken = localStorage.getItem('access');
    axios
      .delete(`${process.env.REACT_APP_API_BASE}/api/bporh/rescisoes/excluir/`, {
        params: {
          cliente_id: empresaSelecionada.cliente_id,
          convenia_id: colaboradorDetalhe.convenia_id,
        },
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then((res) => {
        setMensagem(`${res.data.mensagem}`);
        setColaboradorDetalhe(null);
        carregar();
      })
      .catch((error) => {
        setMensagem(`${error.response?.data?.mensagem || 'Não foi possível excluir a rescisão'}`);
      })
      .finally(() => setExcluindoRescisao(false));
  };
  const [salvandoObsTarefa, setSalvandoObsTarefa] = useState(false);

  const salvarObsTarefa = (campo) => {
    const texto = (obsTarefaEdit?.texto || '').trim();
    setSalvandoObsTarefa(true);
    const accessToken = localStorage.getItem('access');
    axios
      .patch(`${process.env.REACT_APP_API_BASE}/api/bporh/rescisao-checklist/`,
        { ...paramsChecklist(), campo, obs_tarefa: texto },
        { headers: { Authorization: `Bearer ${accessToken}` } })
      .then(() => {
        setChecklist((c) => c && ({
          ...c,
          tarefas: c.tarefas.map(t => t.campo === campo ? { ...t, obs: texto || null } : t),
        }));
        setObsTarefaEdit(null);
      })
      .catch(() => setMensagem('Não foi possível salvar a observação da tarefa'))
      .finally(() => setSalvandoObsTarefa(false));
  };

  const salvarObs = () => {
    setSalvandoObs(true);
    const accessToken = localStorage.getItem('access');
    axios
      .patch(`${process.env.REACT_APP_API_BASE}/api/bporh/rescisao-checklist/`,
        { ...paramsChecklist(), obs: obsEdit },
        { headers: { Authorization: `Bearer ${accessToken}` } })
      .then(() => setMensagem('Observação salva'))
      .catch(() => setMensagem('Não foi possível salvar a observação'))
      .finally(() => setSalvandoObs(false));
  };

  const carregar = useCallback(() => {
    setCarregando(true);
    const accessToken = localStorage.getItem('access');
    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/bporh/rescisoes/`, {
        params: { dias },
        headers: { Authorization: `Bearer ${accessToken}` }
      })
      .then((res) => {
        setEmpresas(res.data.empresas || []);
        setChkOverride({});          // dado do servidor é a verdade agora
        setCarregando(false);
      })
      .catch((error) => {
        const errorMsg =
          error.response?.data?.erro ||
          error.response?.data?.mensagem ||
          error.message ||
          'Erro ao carregar as rescisões';
        setMensagem(`${errorMsg}`);
        setCarregando(false);
      });
  }, [dias]);

  useEffect(() => { carregar(); }, [carregar]);

  // Solicitações do formulário público (/desligamento) pendentes de processamento
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [solicitacaoDetalhe, setSolicitacaoDetalhe] = useState(null);
  const [processando, setProcessando] = useState(false);

  // Formulário de edição da solicitação (a operadora corrige antes de processar —
  // mesmo fluxo da página de Admissões)
  const [solForm, setSolForm] = useState(null);
  const [salvandoSol, setSalvandoSol] = useState(false);
  const [opcoesDesl, setOpcoesDesl] = useState({ tipos_desligamento: [], avisos_previos: [], categorias: [] });

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/desligamento/opcoes/`)
      .then((res) => setOpcoesDesl({
        tipos_desligamento: res.data?.tipos_desligamento || [],
        avisos_previos: res.data?.avisos_previos || [],
        categorias: res.data?.categorias || [],
      }))
      .catch(() => {});
  }, []);

  // colaboradores ATIVOS da empresa da solicitação — alimentam o select
  const [colabsAtivos, setColabsAtivos] = useState([]);
  useEffect(() => {
    if (!solicitacaoDetalhe?.bporh_cliente_id) { setColabsAtivos([]); return; }
    const accessToken = localStorage.getItem('access');
    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/bporh/colaboradores-ativos/`, {
        params: { cliente_id: solicitacaoDetalhe.bporh_cliente_id },
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then((res) => setColabsAtivos(res.data.colaboradores || []))
      .catch(() => setColabsAtivos([]));
  }, [solicitacaoDetalhe?.bporh_cliente_id]);

  useEffect(() => {
    if (!solicitacaoDetalhe) { setSolForm(null); return; }
    setSolForm({
      colaborador: solicitacaoDetalhe.colaborador || '',
      tipo_desligamento: solicitacaoDetalhe.tipo_desligamento || '',
      categoria_desligamento: solicitacaoDetalhe.categoria_desligamento || '',
      aviso_previo: solicitacaoDetalhe.aviso_previo || '',
      data_inicio_aviso: solicitacaoDetalhe.data_inicio_aviso_iso || '',
      data_final_desligamento: solicitacaoDetalhe.data_final_desligamento_iso || '',
      desvincular_beneficios: solicitacaoDetalhe.desvincular_beneficios || '',
      data_remocao_acesso: solicitacaoDetalhe.data_remocao_acesso_iso || '',
      motivo_desligamento: solicitacaoDetalhe.motivo_desligamento || '',
      email_solicitante: solicitacaoDetalhe.email_solicitante || '',
    });
  }, [solicitacaoDetalhe]);

  const setSol = (campo) => (e) => setSolForm((f) => ({ ...f, [campo]: e.target.value }));

  const salvarSolicitacao = () => {
    if (!solForm || !solicitacaoDetalhe) return;
    setSalvandoSol(true);
    const accessToken = localStorage.getItem('access');
    axios
      .patch(`${process.env.REACT_APP_API_BASE}/api/bporh/solicitacoes-desligamento/${solicitacaoDetalhe.id}/`,
        solForm,
        { headers: { Authorization: `Bearer ${accessToken}` } })
      .then(() => {
        setMensagem('Solicitação atualizada com sucesso');
        setSolicitacaoDetalhe(null);
        carregarSolicitacoes();
      })
      .catch((error) => setMensagem(`${error.response?.data?.mensagem || 'Não foi possível salvar'}`))
      .finally(() => setSalvandoSol(false));
  };

  const carregarSolicitacoes = useCallback(() => {
    const accessToken = localStorage.getItem('access');
    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/bporh/solicitacoes-desligamento/`, {
        params: { status: 'Nova' },
        headers: { Authorization: `Bearer ${accessToken}` }
      })
      .then((res) => setSolicitacoes(res.data.solicitacoes || []))
      .catch(() => {});
  }, []);

  useEffect(() => { carregarSolicitacoes(); }, [carregarSolicitacoes]);

  const [confirmandoExclusaoSol, setConfirmandoExclusaoSol] = useState(false);
  useEffect(() => { setConfirmandoExclusaoSol(false); }, [solicitacaoDetalhe]);

  const excluirSolicitacao = () => {
    if (!solicitacaoDetalhe) return;
    setProcessando(true);
    const accessToken = localStorage.getItem('access');
    axios
      .delete(`${process.env.REACT_APP_API_BASE}/api/bporh/solicitacoes-desligamento/${solicitacaoDetalhe.id}/`,
        { headers: { Authorization: `Bearer ${accessToken}` } })
      .then(() => {
        setMensagem('Solicitação excluída');
        setSolicitacaoDetalhe(null);
        carregarSolicitacoes();
      })
      .catch((error) => setMensagem(`${error.response?.data?.mensagem || 'Não foi possível excluir'}`))
      .finally(() => setProcessando(false));
  };

  // ─── Desligamento na Convenia (irreversível: prévia + confirmação) ───
  const [previaConvenia, setPreviaConvenia] = useState(null);
  const [confirmandoConvenia, setConfirmandoConvenia] = useState(false);
  useEffect(() => {
    setPreviaConvenia(null); setConfirmandoConvenia(false); setAjustes(null);
  }, [solicitacaoDetalhe]);

  const urlConvenia = () =>
    `${process.env.REACT_APP_API_BASE}/api/bporh/solicitacoes-desligamento/${solicitacaoDetalhe.id}/criar-convenia/`;

  // campos editáveis da confirmação (não gravam na solicitação — valem só no envio)
  const [ajustes, setAjustes] = useState(null);
  const setAj = (campo) => (e) => setAjustes(a => ({ ...a, [campo]: e.target.value }));

  const verPrevia = () => {
    setProcessando(true);
    const accessToken = localStorage.getItem('access');
    axios
      .post(urlConvenia(), { dry_run: true }, { headers: { Authorization: `Bearer ${accessToken}` } })
      .then((res) => {
        const p = res.data.payload || {};
        setPreviaConvenia(res.data);
        setAjustes({
          tipo_desligamento: res.data.tipo || '',
          aviso_previo: res.data.aviso_previo || '',
          data_final_desligamento: p.dismissal_date || '',
          data_inicio_aviso: p.termination_notice_date || '',
          data_remocao_acesso: p.remove_access_date || '',
          desvincular_beneficios: p.remove_benefit === true ? 'Sim' : p.remove_benefit === false ? 'Não' : '',
          quebra_contrato: Boolean(p.breaking_contract),
          motivo_desligamento: p.motive || '',
        });
        setConfirmandoConvenia(true);
      })
      .catch((error) => setMensagem(`${error.response?.data?.mensagem || 'Não foi possível montar a prévia'}`))
      .finally(() => setProcessando(false));
  };

  const enviarParaConvenia = () => {
    setProcessando(true);
    const accessToken = localStorage.getItem('access');
    axios
      .post(urlConvenia(), { confirmar: true, ajustes }, { headers: { Authorization: `Bearer ${accessToken}` } })
      .then((res) => {
        setMensagem(`${res.data.mensagem}`);
        setSolicitacaoDetalhe(null);
        carregarSolicitacoes();
        carregar();
      })
      .catch((error) => setMensagem(`${error.response?.data?.mensagem || 'Não foi possível criar o desligamento'}`))
      .finally(() => setProcessando(false));
  };

  const marcarProcessada = () => {
    if (!solicitacaoDetalhe) return;
    setProcessando(true);
    const accessToken = localStorage.getItem('access');
    axios
      .patch(
        `${process.env.REACT_APP_API_BASE}/api/bporh/solicitacoes-desligamento/${solicitacaoDetalhe.id}/`,
        { status: 'Processada' },
        { headers: { Authorization: `Bearer ${accessToken}` } })
      .then(() => {
        setMensagem('Solicitação marcada como processada');
        setSolicitacaoDetalhe(null);
        carregarSolicitacoes();
      })
      .catch((error) => setMensagem(`${error.response?.data?.mensagem || 'Não foi possível atualizar'}`))
      .finally(() => setProcessando(false));
  };

  // O estado segue a URL: abre/fecha pela query ?cliente= (inclusive via voltar
  // do navegador) e se mantém atualizado quando a lista recarrega
  useEffect(() => {
    const id = searchParams.get('cliente');
    if (!id) {
      if (empresaSelecionada) {
        setEmpresaSelecionada(null); setBusca('');
        setDias(DIAS_EMPRESAS);          // volta à visão de carteira completa
      }
      return;
    }
    // A janela só é imposta ao ABRIR outra empresa. Comparar por cliente_id (e
    // não pela referência do objeto) é o que importa: trocar o seletor recarrega
    // a lista, recria o objeto da empresa e antes disso a janela voltava sozinha
    // para 30 dias — 'Todo o período' nunca se sustentava.
    if (!empresaSelecionada || String(empresaSelecionada.cliente_id) !== id) {
      const emp = empresas.find(e => String(e.cliente_id) === id);
      if (emp) {
        setEmpresaSelecionada(emp);
        // foco na operação corrente, salvo quando vem da lista de pendências
        if (!periodoAtivo) {
          setDias(searchParams.get('aberto') ? DIAS_EMPRESAS : DIAS_EMPRESA_ABERTA);
        }
      }
    }
  }, [searchParams, empresas]); // eslint-disable-line react-hooks/exhaustive-deps

  // link do e-mail de "Solicitar finalização": ?cliente=X&finalizar=<convenia_id>
  // abre direto o checklist daquele colaborador
  useEffect(() => {
    const conv = searchParams.get('finalizar');
    if (!conv || !empresaSelecionada) return;
    const c = (empresaSelecionada.colaboradores || []).find(x => String(x.convenia_id) === conv);
    if (c && (!colaboradorDetalhe || String(colaboradorDetalhe.convenia_id) !== conv)) {
      setAbaDetalhe('checklist');
      setColaboradorDetalhe(c);
      // vindo do pop-up do Painel do Operador: abre direto o pedido de finalização
      if (searchParams.get('pedir') === '1') setPedirFinalizacao(conv);
    }
  }, [searchParams, empresaSelecionada]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mantém a empresa selecionada em dia quando a lista recarrega (troca de
  // janela, refresh após salvar). Se a nova janela não trouxer desligamentos
  // dela, a empresa continua aberta com a lista vazia em vez de fechar sozinha.
  useEffect(() => {
    if (!empresaSelecionada) return;
    const atualizada = empresas.find(e => e.cliente_id === empresaSelecionada.cliente_id);
    if (atualizada && atualizada !== empresaSelecionada) setEmpresaSelecionada(atualizada);
  }, [empresas]); // eslint-disable-line react-hooks/exhaustive-deps

  const empresasFiltradas = React.useMemo(() =>
    filtroOperador ? empresas.filter(e => e.operador === filtroOperador) : empresas,
    [empresas, filtroOperador]);

  const operadoresDisponiveis = React.useMemo(() =>
    [...new Set(empresas.map(e => e.operador).filter(Boolean))].sort(),
    [empresas]);

  const totalColaboradores = empresasFiltradas.reduce((s, e) => s + (e.total || 0), 0);

  // ─── desligamentos que o checklist ainda não fechou ───
  // o backend já devolve emp.em_aberto / colaborador.checklist
  const emAberto = React.useMemo(() =>
    empresasFiltradas.flatMap(e =>
      (e.colaboradores || [])
        .filter(c => (resumoChk(c).pendentes || 0) > 0)
        .map(c => ({
          cliente_id: e.cliente_id,
          cliente: e.cliente,
          operador: e.operador,
          nome: [c.nome, c.sobrenome].filter(Boolean).join(' ') || c.nome_social || '—',
          data_desligamento: c.data_desligamento,
          checklist: resumoChk(c),
          colaborador: c,
        }))
    ).sort((a, b) => (a.cliente || '').localeCompare(b.cliente || '')),
    [empresasFiltradas, resumoChk]);

  const resumoChecklist = React.useMemo(() => {
    const travas = {};
    emAberto.forEach(d => {
      const t = d.checklist?.proxima;
      if (t) travas[t] = (travas[t] || 0) + 1;
    });
    return {
      pendentes: emAberto.length,
      finalizados: totalColaboradores - emAberto.length,
      tarefas: Object.entries(travas).sort((a, b) => b[1] - a[1]).slice(0, 6),
    };
  }, [emAberto, totalColaboradores]);

  const opcoesCargo = React.useMemo(() =>
    [...new Set((empresaSelecionada?.colaboradores || []).map(c => c.cargo).filter(Boolean))].sort(),
    [empresaSelecionada]);
  const opcoesDepto = React.useMemo(() =>
    [...new Set((empresaSelecionada?.colaboradores || []).map(c => c.departamento).filter(Boolean))].sort(),
    [empresaSelecionada]);

  useEffect(() => {  // troca de empresa limpa os filtros de colaborador
    setFiltroCargo(''); setFiltroDepto(''); setBusca('');
    setFiltroDe(''); setFiltroAte('');
  }, [empresaSelecionada?.cliente_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // mesmo texto que aparece na coluna STATUS, para o filtro casar com o visível
  const statusDoColab = React.useCallback((c) => {
    const ch = resumoChk(c);
    if (ch.ausente) return 'Aguardando execução';
    if (!ch.pendentes) return 'Concluído';
    return ch.proxima || 'Em andamento';
  }, [resumoChk]);

  const opcoesStatus = React.useMemo(() => {
    const lista = empresaSelecionada?.colaboradores || [];
    return [...new Set(lista.map(statusDoColab))].sort((a, b) => a.localeCompare(b));
  }, [empresaSelecionada, statusDoColab]);

  const colaboradoresFiltrados = React.useMemo(() => {
    if (!empresaSelecionada) return [];
    const b = busca.trim().toLowerCase();
    const de = isoOrdenavel(filtroDe);
    const ate = isoOrdenavel(filtroAte);
    return empresaSelecionada.colaboradores
      .filter(c => {
        if (filtroCargo && c.cargo !== filtroCargo) return false;
        if (filtroDepto && c.departamento !== filtroDepto) return false;
        if (filtroStatus && statusDoColab(c) !== filtroStatus) return false;
        if (filtroPrazo) {
          const sit = c.prazo?.situacao;
          if (filtroPrazo === 'pendente' && sit === 'entregue') return false;
          if (filtroPrazo === 'vencendo' && !['atrasado', 'hoje', 'critico', 'alerta'].includes(sit)) return false;
          if (filtroPrazo !== 'pendente' && filtroPrazo !== 'vencendo'
              && sit !== filtroPrazo) return false;
        }
        if (de || ate) {
          // sem data de desligamento fica fora quando há filtro de período
          const d = dataOrdenavel(c.data_desligamento);
          if (!d) return false;
          if (de && d < de) return false;
          if (ate && d > ate) return false;
        }
        if (b && ![c.nome, c.sobrenome, c.cargo, c.departamento, c.email]
          .filter(Boolean).join(' ').toLowerCase().includes(b)) return false;
        return true;
      })
      .sort((a, c) => dataOrdenavel(c.data_desligamento) - dataOrdenavel(a.data_desligamento));
  }, [empresaSelecionada, busca, filtroCargo, filtroDepto, filtroStatus, filtroPrazo,
      filtroDe, filtroAte, statusDoColab]);

  // ─── Exportar para Excel (CSV com BOM — abre direto no Excel) ───
  const exportarExcel = () => {
    const de = isoOrdenavel(filtroDe);
    const ate = isoOrdenavel(filtroAte);
    const noPeriodo = (c) => {
      if (!de && !ate) return true;
      const d = dataOrdenavel(c.data_desligamento);
      if (!d) return false;
      if (de && d < de) return false;
      if (ate && d > ate) return false;
      return true;
    };
    const linhas = empresaSelecionada
      ? colaboradoresFiltrados.map(c => ({ empresa: empresaSelecionada.cliente, operador: empresaSelecionada.operador, c }))
      : empresasFiltradas.flatMap(e =>
          e.colaboradores.filter(noPeriodo).map(c => ({ empresa: e.cliente, operador: e.operador, c })));
    if (linhas.length === 0) {
      setMensagem('Nada para exportar com os filtros atuais');
      return;
    }
    const esc = (v) => `"${String(v ?? '').replaceAll('"', '""')}"`;
    const csv = [
      ['Empresa', 'Operador', 'Colaborador', 'Cargo', 'Departamento',
       'Data Admissão', 'Data Desligamento', 'Status', 'Status Checklist',
       'Atividades Concluídas', 'Atividades Total', 'Parado em', 'Finalizado em',
       'E-mail'].map(esc).join(';'),
      ...linhas.map(({ empresa, operador, c }) => {
        const ch = resumoChk(c);
        const statusChk = ch.ausente ? 'Aguardando execução'
          : ch.pendentes ? 'Em aberto' : 'Finalizado';
        return [
          empresa, operador || '', nomeCompleto(c), c.cargo || '', c.departamento || '',
          c.data_admissao || '', c.data_desligamento || '', c.status || '', statusChk,
          ch.concluidas ?? '', ch.total ?? '', ch.pendentes ? (ch.proxima || '') : '',
          ch.finalizado_em || '', c.email || '',
        ].map(esc).join(';');
      }),
    ].join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    const periodo = periodoAtivo ? `${filtroDe || 'inicio'}_a_${filtroAte || 'hoje'}` : 'todo_periodo';
    a.download = `relatorio_rescisoes_${periodo}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    setMensagem(`Relatório exportado (${linhas.length} desligamento${linhas.length > 1 ? 's' : ''})`);
  };

  const nomeCompleto = (c) =>
    [c.nome, c.sobrenome].filter(Boolean).join(' ') || c.nome_social || '-';

  const formatarMoeda = (v) =>
    v == null ? '-' : `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

  const valorVazio = (v) =>
    v == null || v === '' ||
    (Array.isArray(v) && v.length === 0) ||
    (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0);

  const RenderValor = ({ valor, nivel = 0 }) => {
    if (typeof valor === 'boolean') return <span>{valor ? 'Sim' : 'Não'}</span>;
    if (typeof valor !== 'object') return <span style={{ wordBreak: 'break-word' }}>{String(valor)}</span>;
    if (Array.isArray(valor)) {
      return (
        <div>
          {valor.map((item, i) => (
            <div key={i} className="border rounded p-2 mb-1 bg-white">
              <RenderValor valor={item} nivel={nivel + 1} />
            </div>
          ))}
        </div>
      );
    }
    return (
      <div className="row g-1">
        {Object.entries(valor).filter(([, v]) => !valorVazio(v)).map(([k, v]) => (
          <div key={k} className={typeof v === 'object' ? 'col-12' : 'col-md-4'}>
            <span className="small text-muted">{k}: </span>
            {typeof v === 'object' ? (
              <div className="ps-3 border-start ms-1 mt-1">
                <RenderValor valor={v} nivel={nivel + 1} />
              </div>
            ) : (
              <span className="fw-semibold small"><RenderValor valor={v} nivel={nivel + 1} /></span>
            )}
          </div>
        ))}
      </div>
    );
  };

  const Campo = ({ rotulo, valor, icone }) => (
    <div className="col-md-4 mb-2">
      <div className="small text-muted">
        {icone && <i className={`fas ${icone} me-1`}></i>}
        {rotulo}
      </div>
      <div className="fw-semibold" style={{ wordBreak: 'break-word' }}>{valor || '-'}</div>
    </div>
  );

  return (
    <div className="bporh-rescisoes-page">
      <div className="container-fluid mt-4 shadow p-4 rounded bg-white">
        {/* Cabeçalho */}
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex align-items-center">
            <i className="fas fa-user-minus header-icon me-2"></i>
            <h2 className="titulo-pagina m-0">
              BPO RH — Rescisões
              {empresaSelecionada && <span className="text-muted"> · {empresaSelecionada.cliente}</span>}
            </h2>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Link to="/BpoRhAtividadesAdmissao?tipo=Rescisão" className="btn btn-outline-primary"
              title="Cadastrar o template de atividades de rescisão por cliente">
              <i className="fas fa-list-check me-2"></i>
              Atividades
            </Link>
            {empresaSelecionada && (
              <button className="btn btn-outline-secondary" onClick={fecharEmpresa}>
                <i className="fas fa-arrow-left me-2"></i>
                Empresas
              </button>
            )}
            <select
              className="form-select"
              style={{ width: 'auto' }}
              value={dias}
              onChange={(e) => setDias(parseInt(e.target.value))}
              disabled={periodoAtivo}
              title={periodoAtivo
                ? 'Travado em "Todo o período" enquanto houver filtro de datas'
                : 'Janela de desligamentos'}
            >
              <option value={30}>Últimos 30 dias</option>
              <option value={60}>Últimos 60 dias</option>
              <option value={90}>Últimos 90 dias</option>
              <option value={180}>Últimos 180 dias</option>
              <option value={365}>Últimos 12 meses</option>
              <option value={36500}>Todo o período</option>
            </select>

            {/* Intervalo por data de desligamento — só na visão de colaboradores */}
            {empresaSelecionada && (
              <div className="d-flex align-items-center gap-1 px-2 py-1 rounded"
                style={{ backgroundColor: '#f2f4f8' }}
                title="Filtra pela data de desligamento do colaborador">
                <i className="fas fa-calendar-xmark text-muted" style={{ fontSize: '.82rem' }}></i>
                <input type="date" className="form-control form-control-sm" style={{ width: 'auto' }}
                  value={filtroDe} max={filtroAte || undefined}
                  onChange={(e) => setFiltroDe(e.target.value)} aria-label="Desligamento de" />
                <span className="text-muted small">até</span>
                <input type="date" className="form-control form-control-sm" style={{ width: 'auto' }}
                  value={filtroAte} min={filtroDe || undefined}
                  onChange={(e) => setFiltroAte(e.target.value)} aria-label="Desligamento até" />
                {periodoAtivo && (
                  <button className="btn btn-sm btn-light p-1" title="Limpar período"
                    onClick={() => { setFiltroDe(''); setFiltroAte(''); }}>
                    <i className="fas fa-times text-danger"></i>
                  </button>
                )}
              </div>
            )}
            <button className="btn btn-outline-success" onClick={exportarExcel} disabled={carregando}
              title="Exporta o relatório de desligamentos do período filtrado (abre no Excel)">
              <i className="fas fa-file-excel me-2"></i>
              Exportar Excel
            </button>
            <button className="btn btn-outline-primary" onClick={carregar} disabled={carregando} title="Atualizar">
              <i className={`fas fa-sync-alt ${carregando ? 'fa-spin' : ''}`}></i>
            </button>
          </div>
        </div>

        {carregando ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status"></div>
            <p className="text-muted mt-2 mb-0">Carregando rescisões...</p>
          </div>
        ) : !empresaSelecionada ? (
          <>
            {/* ─── VISÃO 1: EMPRESAS COM DESLIGAMENTOS ─── */}
            <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
              <span className="badge fs-6"
                style={{ backgroundColor: '#00205b', fontVariantNumeric: 'tabular-nums' }}>
                <i className="fas fa-user-minus me-1"></i>
                Total: {totalColaboradores} desligamento{totalColaboradores === 1 ? '' : 's'} no período
              </span>
              <span className="badge bg-secondary" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {empresasFiltradas.length} empresas
              </span>
              <select
                className="form-select form-select-sm ms-auto"
                style={{ maxWidth: '220px' }}
                value={filtroOperador}
                onChange={(e) => setFiltroOperador(e.target.value)}
                title="Filtrar por operador"
              >
                <option value="">Todos os operadores</option>
                {operadoresDisponiveis.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>

            {/* ─── RESUMO DA CARTEIRA ─── */}
            {empresasFiltradas.length > 0 && (
              <div className="adm-resumo">
                <div className="adm-kpi">
                  <span className="v">{totalColaboradores}</span>
                  <span className="k">Desligamentos</span>
                </div>
                <div className="adm-kpi">
                  <span className="v">{empresasFiltradas.length}</span>
                  <span className="k">Empresas</span>
                </div>
                <div className="adm-resumo-sep" />
                <div className="adm-kpi ok">
                  <span className="v">{resumoChecklist.finalizados}</span>
                  <span className="k">Finalizados</span>
                </div>
                <div className="adm-kpi pend">
                  <span className="v">{resumoChecklist.pendentes}</span>
                  <span className="k">Em aberto</span>
                </div>
                <div className="adm-resumo-sep" />
                <div className="adm-resumo-pend">
                  <div className="titulo">
                    {resumoChecklist.pendentes > 0 ? 'Parados nesta atividade' : 'Situação da carteira'}
                  </div>
                  <div className="adm-chips">
                    {resumoChecklist.tarefas.length > 0 ? (
                      resumoChecklist.tarefas.map(([tarefa, qtd]) => (
                        <span key={tarefa} className="adm-chip">
                          <i className="fas fa-clock" style={{ fontSize: '.7rem' }}></i>
                          {tarefa} <b>{qtd}</b>
                        </span>
                      ))
                    ) : (
                      <span className="adm-chip tudo-ok">
                        <i className="fas fa-circle-check" style={{ fontSize: '.72rem' }}></i>
                        Todos os checklists finalizados
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {empresasFiltradas.length === 0 ? (
              <div className="alert alert-info">
                <i className="fas fa-info-circle me-2"></i>
                Nenhum desligamento no período selecionado.
              </div>
            ) : (
              <div className="adm-grid">
                {empresasFiltradas.map((emp, i) => {
                  // desligamentos desta empresa que o checklist ainda não fechou
                  const abertos = emp.colaboradores.filter(
                    c => (resumoChk(c).pendentes || 0) > 0
                  ).length;
                  const finalizados = emp.total - abertos;
                  const pct = emp.total > 0 ? Math.round((finalizados / emp.total) * 100) : 0;
                  // atividade que mais trava as rescisões desta empresa
                  const travas = {};
                  emp.colaboradores.forEach(c => {
                    const ch = resumoChk(c);
                    const t = (ch.pendentes || 0) > 0 ? ch.proxima : null;
                    if (t) travas[t] = (travas[t] || 0) + 1;
                  });
                  const principal = Object.entries(travas).sort((a, b) => b[1] - a[1])[0];
                  return (
                    <div
                      key={emp.cliente_id}
                      className={`adm-card rescisao ${abertos > 0 ? 'tem-pendencia' : 'tudo-ok'}`}
                      style={{ '--i': i }}
                      onClick={() => abrirEmpresa(emp)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter') abrirEmpresa(emp); }}
                    >
                      <div className="adm-card-top">
                        <h3 className="adm-empresa">{emp.cliente}</h3>
                        <span className="adm-total" title="Desligamentos no período">{emp.total}</span>
                      </div>

                      <div className="adm-operador">
                        <i className="fas fa-user-tie"></i>
                        {emp.operador || 'Sem operador'}
                      </div>

                      <div className="adm-progresso" aria-hidden="true">
                        <span style={{ width: `${pct}%` }} />
                      </div>

                      <div className="adm-legenda">
                        {abertos > 0 ? (
                          <div>
                            <span className="pend">
                              <i className="fas fa-clock me-1"></i>
                              {abertos} em aberto
                            </span>
                            <span className="text-muted mx-1">·</span>
                            <span className="text-muted">
                              {finalizados} finalizado{finalizados === 1 ? '' : 's'}
                            </span>
                            {principal && (
                              <span className="adm-pendencia-tarefa">
                                em <b>{principal[0]}</b>
                                {principal[1] > 1 && ` (${principal[1]})`}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="ok">
                            <i className="fas fa-circle-check me-1"></i>
                            Checklist finalizado
                          </span>
                        )}
                      </div>

                      <div className="adm-card-acao">
                        Ver colaboradores <i className="fas fa-arrow-right"></i>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ─── DESLIGAMENTOS COM CHECKLIST EM ABERTO ─── */}
            {emAberto.length > 0 && (
              <div className="mt-4 pt-3 border-top">
                <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
                  <h5 className="fw-bold mb-0" style={{ color: '#212529' }}>
                    <i className="fas fa-list-check me-2" style={{ color: '#f1be48' }}></i>
                    Desligamentos não finalizados
                  </h5>
                  <span className="badge bg-warning text-dark"
                    style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {emAberto.length} em aberto
                  </span>
                  <span className="badge bg-light text-dark border">checklist de rescisão</span>
                </div>

                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Empresa</th>
                        <th>Colaborador</th>
                        <th>Desligamento</th>
                        <th>Operador</th>
                        <th style={{ minWidth: '160px' }}>Checklist</th>
                        <th>Parado em</th>
                        <th className="text-end">Ação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {emAberto.map((d) => {
                        const ch = d.checklist || {};
                        const pct = ch.total ? Math.round((ch.concluidas / ch.total) * 100) : 0;
                        return (
                          <tr key={`${d.cliente_id}-${d.colaborador?.convenia_id || d.nome}`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => abrirDesligamentoAberto(d)}>
                            <td className="fw-semibold" style={{ color: '#00205b' }}>{d.cliente}</td>
                            <td>{d.nome}</td>
                            <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                              {d.data_desligamento || '—'}
                            </td>
                            <td className="text-muted">{d.operador || '—'}</td>
                            <td>
                              <div className="adm-progresso" style={{ marginBottom: '4px' }}>
                                <span style={{ width: `${pct}%`, background: '#f1be48' }} />
                              </div>
                              <small className="text-muted" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                {ch.concluidas || 0}/{ch.total || 0} concluídas
                              </small>
                            </td>
                            <td>
                              <span className="badge"
                                style={{ backgroundColor: '#fff4d6', color: '#8a6d00', border: '1px solid #f1be48' }}>
                                {ch.proxima || '—'}
                              </span>
                            </td>
                            <td className="text-end">
                              <button className="btn btn-sm btn-outline-primary"
                                onClick={(e) => { e.stopPropagation(); abrirDesligamentoAberto(d); }}
                                title="Abrir checklist">
                                <i className="fas fa-list-check"></i>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ─── SOLICITAÇÕES DO FORMULÁRIO PÚBLICO (/desligamento) ─── */}
            {solicitacoes.length > 0 && (
              <div className="mt-4 pt-3 border-top">
                <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
                  <h5 className="fw-bold mb-0" style={{ color: '#212529' }}>
                    <i className="fas fa-inbox me-2 text-warning"></i>
                    Solicitações de desligamento recebidas
                  </h5>
                  <span className="badge bg-warning text-dark">{solicitacoes.length} nova(s)</span>
                  <span className="badge bg-light text-dark border">
                    formulário público <code>/desligamento</code>
                  </span>
                </div>
                <div className="table-responsive">
                  <table className="table table-striped table-hover align-middle">
                    <thead>
                      <tr>
                        <th><i className="fas fa-user me-2"></i>Colaborador</th>
                        <th><i className="fas fa-id-badge me-2"></i>Verificação</th>
                        <th><i className="fas fa-building me-2"></i>Empresa</th>
                        <th><i className="fas fa-file-signature me-2"></i>Tipo de desligamento</th>
                        <th><i className="fas fa-bullhorn me-2"></i>Aviso prévio</th>
                        <th><i className="fas fa-calendar-day me-2"></i>Início aviso</th>
                        <th><i className="fas fa-calendar-xmark me-2"></i>Data final</th>
                        <th><i className="fas fa-user-lock me-2"></i>Remover acesso</th>
                        <th><i className="fas fa-clock me-2"></i>Recebida em</th>
                        <th><i className="fas fa-flag me-2"></i>Status</th>
                        <th className="text-center">Detalhes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {solicitacoes.map((s) => (
                        <tr key={s.id} style={{ cursor: 'pointer' }}
                          onClick={() => setSolicitacaoDetalhe(s)}>
                          <td className="fw-bold">{s.colaborador}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            <BadgeVerificacao s={s} />
                          </td>
                          <td>
                            {s.cliente || s.empresa}
                            {!s.cliente && (
                              <div>
                                <span className="badge bg-light text-danger border mt-1"
                                  title="O nome informado não casou com nenhum cliente da carteira">
                                  sem vínculo
                                </span>
                              </div>
                            )}
                          </td>
                          {/* tipos da Convenia são frases longas: trunca e mostra inteiro no title */}
                          <td style={{ maxWidth: '260px' }} title={s.tipo_desligamento}>
                            <span className="d-inline-block text-truncate w-100">
                              {s.tipo_desligamento}
                            </span>
                          </td>
                          <td>
                            <span className="badge bg-light text-dark border">{s.aviso_previo}</span>
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>{s.data_inicio_aviso || '-'}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>{s.data_final_desligamento || '-'}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>{s.data_remocao_acesso || '-'}</td>
                          <td className="text-muted" style={{ fontSize: '.82rem', whiteSpace: 'nowrap' }}>
                            {s.created_at}
                          </td>
                          <td>
                            <span className={`badge ${s.status === 'Processada' ? 'bg-success' : 'bg-warning text-dark'}`}>
                              {s.status}
                            </span>
                          </td>
                          <td className="text-center">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={(e) => { e.stopPropagation(); setSolicitacaoDetalhe(s); }}
                              title="Ver a solicitação completa"
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {/* ─── VISÃO 2: COLABORADORES DESLIGADOS DA EMPRESA ─── */}
            <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
              <span className="badge bg-primary fs-6">
                <i className="fas fa-building me-1"></i> {empresaSelecionada.cliente}
              </span>
              <span className="badge bg-secondary">
                <i className="fas fa-user-tie me-1"></i> {empresaSelecionada.operador || 'Sem operador'}
              </span>
              <span className="badge fs-6" style={{ backgroundColor: '#00205b' }}>
                <i className="fas fa-user-minus me-1"></i>
                {colaboradoresFiltrados.length} desligamento{colaboradoresFiltrados.length === 1 ? '' : 's'} no período
              </span>
            </div>

            {/* Atalhos que preenchem os dois campos de data do cabeçalho */}
            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
              <span className="small text-muted fw-bold">Período rápido:</span>
              {[
                { rotulo: 'Este mês', meses: 0 },
                { rotulo: 'Últimos 3 meses', meses: 3 },
                { rotulo: 'Este ano', ano: true },
              ].map((a) => (
                <button key={a.rotulo} className="btn btn-sm btn-outline-secondary"
                  onClick={() => {
                    const hoje = new Date();
                    const iso = (d) => d.toISOString().slice(0, 10);
                    if (a.ano) {
                      setFiltroDe(`${hoje.getFullYear()}-01-01`);
                    } else if (a.meses === 0) {
                      setFiltroDe(iso(new Date(hoje.getFullYear(), hoje.getMonth(), 1)));
                    } else {
                      setFiltroDe(iso(new Date(hoje.getFullYear(), hoje.getMonth() - a.meses, hoje.getDate())));
                    }
                    setFiltroAte(iso(hoje));
                  }}>
                  {a.rotulo}
                </button>
              ))}
            </div>

            {periodoAtivo && (
              <div className="alert alert-info py-2 small">
                <i className="fas fa-filter me-2"></i>
                Mostrando desligamentos
                {filtroDe && <> a partir de <b>{filtroDe.split('-').reverse().join('/')}</b></>}
                {filtroAte && <> até <b>{filtroAte.split('-').reverse().join('/')}</b></>}
                {' '}sobre <b>todo o período</b> (a janela do cabeçalho fica travada
                enquanto o filtro de datas estiver ativo). Colaboradores sem data de
                desligamento ficam fora deste filtro.
              </div>
            )}

            {/* Filtros da tabela */}
            <div className="row g-2 mb-3">
              <div className="col-md-4">
                <label className="form-label small fw-bold mb-1 text-muted">Cargo</label>
                <select className="form-select form-select-sm" value={filtroCargo}
                  onChange={(e) => setFiltroCargo(e.target.value)}>
                  <option value="">Todos os cargos</option>
                  {opcoesCargo.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label small fw-bold mb-1 text-muted">Departamento</label>
                <select className="form-select form-select-sm" value={filtroDepto}
                  onChange={(e) => setFiltroDepto(e.target.value)}>
                  <option value="">Todos os departamentos</option>
                  {opcoesDepto.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label small fw-bold mb-1 text-muted">Buscar</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Nome, cargo, e-mail..."
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label small fw-bold mb-1 text-muted">Status</label>
                <select className="form-select form-select-sm" value={filtroStatus}
                  onChange={(e) => setFiltroStatus(e.target.value)}>
                  <option value="">Todos os status</option>
                  {opcoesStatus.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label small fw-bold mb-1 text-muted">Prazo</label>
                <select className="form-select form-select-sm" value={filtroPrazo}
                  onChange={(e) => setFiltroPrazo(e.target.value)}>
                  <option value="">Todos os prazos</option>
                  <option value="pendente">Ainda não entregue</option>
                  <option value="vencendo">Vencendo ou atrasado</option>
                  <option value="critico">Crítico (faltam 2 dias ou menos)</option>
                  <option value="atrasado">Só atrasados</option>
                  <option value="hoje">Vence hoje</option>
                  <option value="entregue">Entregues</option>
                </select>
              </div>
              {(filtroStatus || filtroPrazo || filtroCargo || filtroDepto) && (
                <div className="col-md-4 d-flex align-items-end">
                  <button className="btn btn-sm btn-light border"
                    onClick={() => { setFiltroStatus(''); setFiltroPrazo('');
                      setFiltroCargo(''); setFiltroDepto(''); }}>
                    <i className="fas fa-filter-circle-xmark me-2"></i>Limpar filtros
                  </button>
                </div>
              )}
            </div>

            <div className="table-responsive">
              <table className="table table-striped table-hover align-middle">
                <thead>
                  <tr>
                    <th><i className="fas fa-user me-2"></i>Colaborador</th>
                    <th><i className="fas fa-briefcase me-2"></i>Cargo</th>
                    <th><i className="fas fa-sitemap me-2"></i>Departamento</th>
                    <th><i className="fas fa-file-signature me-2"></i>Vínculo</th>
                    {/* Data de admissão fica só na aba Dados do colaborador:
                        na rescisão o que a listagem cobra é a data do
                        desligamento. */}
                    <th><i className="fas fa-calendar-times me-2"></i>Desligamento</th>
                    <th><i className="fas fa-calendar-check me-2"></i>Entregar até</th>
                    <th><i className="fas fa-stopwatch me-2"></i>Prazo</th>
                    <th><i className="fas fa-flag me-2"></i>Status</th>
                    <th><i className="fas fa-list-check me-2"></i>Tarefas</th>
                    <th className="text-center">Detalhes</th>
                  </tr>
                </thead>
                <tbody>
                  {colaboradoresFiltrados.map((c) => (
                    <tr key={c.convenia_id} style={{ cursor: 'pointer' }}
                      onClick={() => { setAbaDetalhe('checklist'); setColaboradorDetalhe(c); }}>
                      <td className="fw-bold">{nomeCompleto(c)}</td>
                      <td>{c.cargo || '-'}</td>
                      <td>{c.departamento || '-'}</td>
                      <td><VinculoBadge vinculo={c.vinculo} origem={c.vinculo_origem} /></td>
                      <td>
                        <span className="badge bg-danger">
                          {c.data_desligamento || '-'}
                        </span>
                      </td>
                      <td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
                        title={c.prazo?.rotulo || ''}>
                        {c.prazo?.limite || <span className="text-muted">—</span>}
                      </td>
                      <td><PrazoBadge prazo={c.prazo} /></td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {(() => {
                          // sem registro ainda = checklist recém-nascido do modelo do
                          // cliente (igual às Admissões): mostra as tarefas, não 'sem checklist'
                          const ch = resumoChk(c);
                          if (!ch.pendentes) {
                            return (
                              <span className="badge bg-success" title="Checklist de rescisão finalizado">
                                <i className="fas fa-check me-1"></i>
                                Concluído{ch.finalizado_em && <> · {ch.finalizado_em}</>}
                              </span>
                            );
                          }
                          return (
                            <span className="badge bg-warning text-dark"
                              title={`${ch.pendentes} atividade(s) pendente(s)`}>
                              <i className="fas fa-clock me-1"></i>
                              {ch.proxima}
                              {ch.pendentes > 1 && <> +{ch.pendentes - 1}</>}
                            </span>
                          );
                        })()}
                      </td>
                      <td>
                        {(() => {
                          // sem registro ainda = checklist recém-nascido do modelo do
                          // cliente (igual às Admissões): mostra as tarefas, não 'sem checklist'
                          const ch = resumoChk(c);
                          const completo = ch.total > 0 && ch.concluidas === ch.total;
                          return (
                            <span
                              className={`badge ${completo ? 'bg-success'
                                : ch.concluidas > 0 ? 'bg-warning text-dark' : 'bg-secondary'}`}
                              title="Progresso do checklist de rescisão"
                            >
                              <i className={`fas ${completo ? 'fa-check-double' : 'fa-list-check'} me-1`}></i>
                              {ch.concluidas}/{ch.total}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="text-center">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={(e) => { e.stopPropagation(); setAbaDetalhe('dados'); setColaboradorDetalhe(c); }}
                          title="Ver dados do colaborador"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* ─── MODAL: DADOS DO COLABORADOR DESLIGADO ─── */}
      {colaboradorDetalhe && (
        <div
          className="modal"
          tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-id-card me-2"></i>
                    {nomeCompleto(colaboradorDetalhe)}
                  </h5>
                  <small style={{ color: '#6c757d' }}>
                    {empresaSelecionada?.cliente} · dados espelhados da Convenia
                    {colaboradorDetalhe.sincronizado_em && ` · sincronizado em ${colaboradorDetalhe.sincronizado_em}`}
                  </small>
                </div>
                <button type="button" className="btn-close" onClick={() => setColaboradorDetalhe(null)}></button>
              </div>

              <div className="modal-body">
                {/* Abas: Checklist (operação) e Dados (espelho da Convenia) */}
                <ul className="nav nav-tabs mb-3">
                  <li className="nav-item">
                    <button type="button"
                      className={`nav-link ${abaDetalhe === 'checklist' ? 'active fw-bold' : ''}`}
                      onClick={() => setAbaDetalhe('checklist')}>
                      <i className="fas fa-list-check me-2"></i>Checklist
                    </button>
                  </li>
                  <li className="nav-item">
                    <button type="button"
                      className={`nav-link ${abaDetalhe === 'dados' ? 'active fw-bold' : ''}`}
                      onClick={() => setAbaDetalhe('dados')}>
                      <i className="fas fa-id-card me-2"></i>Dados
                    </button>
                  </li>
                </ul>

                {abaDetalhe === 'checklist' && !carregandoCheck && (
                  <FinalizacaoManual tipo="rescisao" clienteId={empresaSelecionada?.cliente_id}
                    conveniaId={colaboradorDetalhe.convenia_id}
                    pendentes={(checklist?.tarefas || []).filter(t => !atividadeFechada(t.valor)).map(t => t.rotulo)}
                    abrirSolicitar={pedirFinalizacao === String(colaboradorDetalhe.convenia_id)}
                    onMudou={() => { setColaboradorDetalhe(null); carregar(); }} />
                )}
                {abaDetalhe === 'checklist' && (
                  carregandoCheck ? (
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary" role="status"></div>
                      <p className="text-muted mt-2 mb-0">Carregando checklist...</p>
                    </div>
                  ) : !checklist ? (
                    <div className="alert alert-warning">
                      <i className="fas fa-triangle-exclamation me-2"></i>
                      Não foi possível carregar o checklist desta rescisão.
                    </div>
                  ) : (<>
                    {/* cabeçalho de progresso — mesmo formato do checklist da admissão */}
                    {(() => {
                      // 'Não se aplica' fecha a tarefa igual a 'Concluído' — o
                      // percentual conta as duas (atividadeFechada normaliza acento)
                      const done = checklist.tarefas.filter(t => atividadeFechada(t.valor)).length;
                      const na = checklist.tarefas.filter(t => {
                        const n = (t.valor || '').normalize('NFD').replace(SEM_ACENTO, '').trim().toLowerCase();
                        return n.startsWith('nao se aplica');
                      }).length;
                      const total = checklist.tarefas.length;
                      const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                      const completo = total > 0 && done === total;
                      return (
                        <div className="mb-3">
                          <div className="d-flex justify-content-between align-items-center mb-1 flex-wrap gap-2">
                            <span className="fw-semibold" style={{ color: '#212529' }}>
                              <i className="fas fa-list-check me-2 text-muted"></i>
                              {done}/{total} tarefas concluídas
                              {na > 0 && (
                                <span className="text-muted fw-normal small"> ({na} não se aplica{na > 1 ? 'm' : ''})</span>
                              )}
                              {checklist.dt_desligamento && (
                                <span className="text-muted fw-normal"> · desligamento {checklist.dt_desligamento}</span>
                              )}
                            </span>
                            <span className={`badge ${completo ? 'bg-success' : 'bg-warning text-dark'}`}>{pct}%</span>
                          </div>
                          <div className="progress" style={{ height: '6px' }}>
                            <div
                              className={`progress-bar ${completo ? 'bg-success' : 'bg-warning'}`}
                              style={{ width: `${pct}%` }}
                              role="progressbar"
                            />
                          </div>
                        </div>
                      );
                    })()}

                    <div className="border rounded overflow-hidden mb-3">
                      {checklist.tarefas.map((t, i) => {
                        const v = (t.valor || '').trim();
                        const concl = v.toLowerCase().startsWith('conclu');
                        const naoAplica = v.toLowerCase().replace('í', 'i').startsWith('nao se aplica');
                        const OPCOES = [
                          { valor: 'Concluído', icone: 'fa-check', classe: 'success', titulo: 'Concluído' },
                          { valor: 'Solicitado', icone: 'fa-paper-plane', classe: 'info', titulo: 'Solicitado — pedido feito, aguardando terceiro' },
                          ...(t.campo === 'ex_demissional'
                            ? [{ valor: 'Agendado', icone: 'fa-calendar-check', classe: 'primary', titulo: 'Exame agendado na clínica' }]
                            : []),
                          { valor: 'Não iniciada', icone: 'fa-clock', classe: 'warning', titulo: 'Não iniciada' },
                          // rescisão recente EXIGE o contrato: sem a saída
                          // 'Não se aplica', a única forma de fechar é anexando
                          ...(t.campo === 'contrato_assinado'
                              && contratoExigido(colaboradorDetalhe?.data_desligamento,
                            colaboradorDetalhe?.contratacao || colaboradorDetalhe?.vinculo)
                            ? []
                            : [{ valor: 'Não se aplica', icone: 'fa-minus', classe: 'secondary', titulo: 'Não se aplica' }]),
                        ];
                        const atual = OPCOES.some(o => o.valor === v) ? v
                          : concl ? 'Concluído' : naoAplica ? 'Não se aplica' : 'Não iniciada';
                        return (
                          <div
                            key={t.campo}
                            className="d-flex align-items-center gap-3 px-3 py-2"
                            style={{
                              borderTop: i === 0 ? 'none' : '1px solid #f1f3f5',
                              background: naoAplica ? '#fbfbfc' : '#fff',
                            }}
                          >
                            <i className={`fas ${
                              concl ? 'fa-circle-check text-success' :
                              naoAplica ? 'fa-circle-minus text-secondary opacity-50' :
                              v === 'Solicitado' ? 'fa-circle-dot text-info' :
                              v === 'Agendado' ? 'fa-circle-dot text-primary' : 'fa-circle text-warning opacity-75'
                            }`} style={{ fontSize: '0.9rem' }}></i>

                            <div style={{ minWidth: '210px', flex: 1 }}>
                              <div
                                className={naoAplica ? 'text-muted' : 'fw-semibold'}
                                style={{ fontSize: '0.92rem', color: naoAplica ? undefined : '#212529' }}
                              >
                                {t.rotulo}
                                {v === 'Solicitado' && (
                                  <span className="badge ms-2"
                                    style={{ backgroundColor: '#e3f6fb', color: '#0e6e86',
                                      border: '1.5px solid #00b5cc', fontSize: '.7rem' }}>
                                    <i className="fas fa-paper-plane me-1"></i>Solicitado
                                  </span>
                                )}
                                {v === 'Agendado' && (
                                  <span className="badge ms-2"
                                    style={{ backgroundColor: '#eef3ff', color: '#1d4ed8',
                                      border: '1.5px solid #3b82f6', fontSize: '.7rem' }}>
                                    <i className="fas fa-calendar-check me-1"></i>Agendado
                                  </span>
                                )}
                                {v && !OPCOES.some(o => o.valor === v) && !concl && !naoAplica && (
                                  <span className="badge bg-info-subtle text-dark border ms-2"
                                    style={{ fontSize: '.7rem' }} title="Valor herdado do histórico">
                                    {v}
                                  </span>
                                )}
                              </div>
                              {obsTarefaEdit?.campo === t.campo ? (
                                <div className="d-flex align-items-center gap-1 mt-1">
                                  <input type="text" className="form-control form-control-sm"
                                    style={{ fontSize: '.78rem', maxWidth: '280px' }}
                                    maxLength={500} autoFocus
                                    placeholder="Observação desta atividade..."
                                    value={obsTarefaEdit.texto}
                                    disabled={salvandoObsTarefa}
                                    onChange={(e) => setObsTarefaEdit(o => ({ ...o, texto: e.target.value }))}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') salvarObsTarefa(t.campo);
                                      if (e.key === 'Escape') setObsTarefaEdit(null);
                                    }} />
                                  <button className="btn btn-sm btn-success p-1 px-2"
                                    disabled={salvandoObsTarefa}
                                    onClick={() => salvarObsTarefa(t.campo)} title="Salvar (Enter)">
                                    <i className={`fas ${salvandoObsTarefa ? 'fa-spinner fa-spin' : 'fa-check'}`}></i>
                                  </button>
                                  <button className="btn btn-sm btn-light border p-1 px-2"
                                    disabled={salvandoObsTarefa}
                                    onClick={() => setObsTarefaEdit(null)} title="Cancelar (Esc)">
                                    <i className="fas fa-times text-muted"></i>
                                  </button>
                                </div>
                              ) : t.obs ? (
                                <div className="text-muted mt-1"
                                  style={{ fontSize: '.76rem', cursor: 'pointer' }}
                                  title="Clique para editar a observação"
                                  onClick={() => setObsTarefaEdit({ campo: t.campo, texto: t.obs })}>
                                  <i className="fas fa-comment-dots me-1" style={{ color: '#00b5cc' }}></i>
                                  {t.obs}
                                </div>
                              ) : null}

                              {/* contrato de rescisão: PDF obrigatório para concluir */}
                              {t.campo === 'contrato_assinado' && (
                                <ContratoAnexo
                                  tipo="rescisao"
                                  clienteId={empresaSelecionada?.cliente_id || colaboradorDetalhe.cliente_id}
                                  conveniaId={colaboradorDetalhe.convenia_id}
                                  colaborador={[colaboradorDetalhe.nome, colaboradorDetalhe.sobrenome]
                                    .filter(Boolean).join(' ')}
                                  vinculo={colaboradorDetalhe.contratacao || colaboradorDetalhe.vinculo}
                                  aviso={t.valor !== 'Concluído'}
                                  onEstado={(e) => { setTemContrato(e.tem); setExigeContrato(e.exige !== false); }}
                                  onMudou={() => carregar()}
                                />
                              )}
                            </div>

                            {/* automações: largura fixa em TODAS as linhas para alinhar os botões de status */}
                            <div style={{ width: '170px', flexShrink: 0 }}>
                              {t.campo === 'previa_rescisao' && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary w-100"
                                  style={{ whiteSpace: 'normal', lineHeight: 1.15, fontSize: '0.78rem', padding: '3px 6px' }}
                                  onClick={() => setEmailPrevia({
                                    cliente_id: empresaSelecionada?.cliente_id || colaboradorDetalhe.cliente_id,
                                    convenia_id: colaboradorDetalhe.convenia_id,
                                    nome: [colaboradorDetalhe.nome, colaboradorDetalhe.sobrenome].filter(Boolean).join(' '),
                                  })}
                                  title="E-mail ao cliente com a prévia da rescisão, verbas variáveis e anexos para conferência"
                                >
                                  <i className="fas fa-envelope-open-text me-2"></i>Enviar prévia
                                </button>
                              )}
                              {t.campo === 'assinatura' && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary w-100"
                                  style={{ whiteSpace: 'normal', lineHeight: 1.15, fontSize: '0.78rem', padding: '3px 6px' }}
                                  onClick={() => setEmailColab({
                                    cliente_id: empresaSelecionada?.cliente_id || colaboradorDetalhe.cliente_id,
                                    convenia_id: colaboradorDetalhe.convenia_id,
                                    nome: [colaboradorDetalhe.nome, colaboradorDetalhe.sobrenome].filter(Boolean).join(' '),
                                  })}
                                  title="Envia ao colaborador o e-mail com os documentos da rescisão (TRCT, demonstrativo, guias) para conferência e assinatura"
                                >
                                  <i className="fas fa-file-signature me-2"></i>Enviar ao colaborador
                                </button>
                              )}
                              {t.campo === 'aviso_carta' && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary w-100"
                                  style={{ whiteSpace: 'normal', lineHeight: 1.15, fontSize: '0.78rem', padding: '3px 6px' }}
                                  onClick={() => setCartaDesl({
                                    cliente_id: empresaSelecionada?.cliente_id || colaboradorDetalhe.cliente_id,
                                    convenia_id: colaboradorDetalhe.convenia_id,
                                    nome: [colaboradorDetalhe.nome, colaboradorDetalhe.sobrenome].filter(Boolean).join(' '),
                                  })}
                                  title="Pede ao colaborador a carta de desligamento de próprio punho — só quando o desligamento é pedido do empregado (conferido na Convenia)"
                                >
                                  <i className="fas fa-envelope-open-text me-2"></i>Pedir carta
                                </button>
                              )}
                              {t.campo === 'ex_demissional' && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary w-100"
                                  style={{ whiteSpace: 'normal', lineHeight: 1.15, fontSize: '0.78rem', padding: '3px 6px' }}
                                  disabled={exameDemCarregando}
                                  onClick={() => abrirExameDem(colaboradorDetalhe)}
                                  title="Pedir o exame demissional à clínica de medicina do trabalho, com a guia em PDF (mesmo fluxo do Exame Mensal)"
                                >
                                  {exameDemCarregando
                                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Carregando...</>
                                    : <><i className="fas fa-stethoscope me-2"></i>Solicitar exame</>}
                                </button>
                              )}
                              {Object.entries(BENEF_RESC).filter(([, cfg]) => cfg.campo === t.campo).map(([tipo, cfg]) => (
                                <button
                                  key={tipo}
                                  type="button"
                                  className="btn btn-sm btn-outline-primary w-100 mb-1"
                                  style={{ whiteSpace: 'normal', lineHeight: 1.15, fontSize: '0.78rem', padding: '3px 6px' }}
                                  disabled={benefResc?.carregando}
                                  onClick={() => abrirBenefResc(colaboradorDetalhe, tipo)}
                                  title={`Quanto de ${cfg.titulo} foi pago e quanto caiu depois do desligamento (para descontar na rescisão)`}
                                >
                                  {benefResc?.carregando && benefResc?.tipo === tipo
                                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Carregando...</>
                                    : <><i className={`fas ${cfg.icone} me-2`}></i>{cfg.botao}</>}
                                </button>
                              ))}
                              {t.campo === 'ponto' && sistemasPontoApi.length > 0 && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger w-100"
                                  style={{ whiteSpace: 'normal', lineHeight: 1.15, fontSize: '0.78rem', padding: '3px 6px' }}
                                  disabled={desligPonto?.carregando}
                                  onClick={() => abrirDesligarPonto(colaboradorDetalhe)}
                                  title={`Tirar este colaborador do ${sistemasPontoApi.map(x => x.rotulo).join(' / ')} (mostra a prévia antes)`}
                                >
                                  {desligPonto?.carregando
                                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Carregando...</>
                                    : <><i className="fas fa-user-slash me-2"></i>Desligar do ponto</>}
                                </button>
                              )}
                              {t.campo === 'ponto' && sistemasPontoApi.length === 0 && (empresaSelecionada?.sistemas_ponto || []).length > 0 && (
                                <span className="badge bg-light text-muted border w-100 py-2 mb-1"
                                  style={{ fontSize: '0.72rem', whiteSpace: 'normal' }}
                                  title="Este sistema não tem desligamento por API — inative o colaborador no portal">
                                  <i className="fas fa-hand-pointer me-1"></i>Desligamento manual
                                </span>
                              )}
                              {t.campo === 'convenia' && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger w-100 mb-1"
                                  style={{ whiteSpace: 'normal', lineHeight: 1.15, fontSize: '0.78rem', padding: '3px 6px' }}
                                  disabled={desligConv?.carregando}
                                  onClick={() => abrirDesligarConvenia(colaboradorDetalhe)}
                                  title="Cria o desligamento deste colaborador na Convenia (mostra a prévia e pede confirmação — irreversível)"
                                >
                                  {desligConv?.carregando
                                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Carregando...</>
                                    : <><i className="fas fa-user-slash me-2"></i>Desligar na Convenia</>}
                                </button>
                              )}
                              {portaisDaTarefa(t.campo).map((pt, k) => {
                                const cred = credPortal[pt.id] || {};
                                const temCred = !!(pt.login || pt.tem_senha);
                                return (
                                  <div key={`${pt.portal}-${k}`} className={k > 0 || t.campo === 'ex_demissional' || (t.campo === 'ponto' && sistemasPontoApi.length > 0) ? 'mt-1' : ''}>
                                    <div className="d-flex gap-1">
                                      <a href={pt.portal} target="_blank" rel="noreferrer"
                                        className="btn btn-sm btn-outline-secondary flex-grow-1"
                                        style={{ fontSize: '0.78rem', padding: '3px 6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}
                                        title={`Abrir ${pt.propriedade} em nova aba (portal cadastrado em Serviços)`}>
                                        <i className="fas fa-arrow-up-right-from-square me-2"></i>{pt.propriedade}
                                      </a>
                                      {temCred && (
                                        <button type="button"
                                          className={`btn btn-sm ${cred.aberto ? 'btn-secondary' : 'btn-outline-secondary'} flex-shrink-0`}
                                          style={{ padding: '3px 7px', fontSize: '0.78rem' }}
                                          onClick={() => alternarCredPortal(pt)}
                                          title={cred.aberto ? 'Ocultar login e senha' : `Ver login e senha do ${pt.propriedade} (o acesso fica registrado)`}>
                                          <i className={`fas ${cred.carregando ? 'fa-spinner fa-spin' : 'fa-key'}`}></i>
                                        </button>
                                      )}
                                    </div>
                                    {cred.aberto && !cred.carregando && (
                                      <div className="border rounded px-2 py-1 mt-1 bg-light" style={{ fontSize: '0.74rem' }}>
                                        <div className="d-flex align-items-center gap-1">
                                          <span className="text-muted">login</span>
                                          <code className="text-truncate" style={{ maxWidth: 80 }} title={pt.login || ''}>{pt.login || '—'}</code>
                                          {pt.login && (
                                            <button type="button" className="btn btn-sm btn-light border p-0 px-1 ms-auto" onClick={() => copiarCred(pt.login, 'Login')} title="Copiar login">
                                              <i className="fas fa-copy text-muted" style={{ fontSize: '.68rem' }}></i>
                                            </button>
                                          )}
                                        </div>
                                        <div className="d-flex align-items-center gap-1">
                                          <span className="text-muted">senha</span>
                                          <code className="text-truncate" style={{ maxWidth: 80 }}>{pt.tem_senha ? (cred.senha || '—') : '—'}</code>
                                          {cred.senha && (
                                            <button type="button" className="btn btn-sm btn-light border p-0 px-1 ms-auto" onClick={() => copiarCred(cred.senha, 'Senha')} title="Copiar senha">
                                              <i className="fas fa-copy text-muted" style={{ fontSize: '.68rem' }}></i>
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>

                            {!t.obs && obsTarefaEdit?.campo !== t.campo && (
                              <button className="btn btn-sm btn-light border p-1 px-2 flex-shrink-0"
                                onClick={() => setObsTarefaEdit({ campo: t.campo, texto: '' })}
                                title="Adicionar observação a esta atividade">
                                <i className="far fa-comment text-muted" style={{ fontSize: '.8rem' }}></i>
                              </button>
                            )}

                            <div className="btn-group btn-group-sm flex-shrink-0" role="group">
                              {OPCOES.map(o => {
                                // 'Concluído' do contrato só destrava com o PDF anexado
                                const travado = t.campo === 'contrato_assinado'
                                  && o.valor === 'Concluído' && exigeContrato && !temContrato;
                                return (
                                <button
                                  key={o.valor}
                                  type="button"
                                  title={travado ? 'Anexe o PDF do contrato para concluir' : o.titulo}
                                  disabled={marcando === t.campo || travado}
                                  // btn-sm é obrigatório: theme.css dá min-width:170px a
                                  // '.d-flex .btn:not(.btn-sm)' e o grupo estouraria a linha
                                  className={`btn btn-sm ${atual === o.valor ? `btn-${o.classe}` : 'btn-light border'}`}
                                  style={{ padding: '5px 22px', minWidth: 'auto' }}
                                  onClick={() => marcarCampo(t.campo, o.valor)}
                                >
                                  <i className={`fas ${o.icone}`} style={{
                                    fontSize: '0.85rem',
                                    color: atual === o.valor ? undefined : '#adb5bd',
                                  }}></i>
                                </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Observação da rescisão */}
                    <label className="form-label fw-bold">
                      <i className="fas fa-comment-dots me-2"></i>Observação
                    </label>
                    <textarea className="form-control" rows={3} value={obsEdit}
                      onChange={(e) => setObsEdit(e.target.value)} disabled={salvandoObs}
                      placeholder="Contexto da rescisão, pendências, combinados com o cliente..." />
                    <div className="d-flex justify-content-end mt-2">
                      <button className="btn btn-sm btn-primary" onClick={salvarObs} disabled={salvandoObs}>
                        {salvandoObs
                          ? <><span className="spinner-border spinner-border-sm me-2"></span>Salvando...</>
                          : <><i className="fas fa-save me-2"></i>Salvar observação</>}
                      </button>
                    </div>
                  </>)
                )}

                {abaDetalhe === 'dados' && (<>
                <h6 className="fw-bold text-danger border-bottom pb-1 mb-3">
                  <i className="fas fa-user-minus me-2"></i>Desligamento
                </h6>
                <div className="row">
                  <Campo rotulo="Data do Desligamento" valor={colaboradorDetalhe.data_desligamento} icone="fa-calendar-times" />
                  <Campo rotulo="Data de Admissão" valor={colaboradorDetalhe.data_admissao} icone="fa-calendar-plus" />
                  <Campo rotulo="Status" valor={colaboradorDetalhe.status} icone="fa-flag" />
                </div>

                <h6 className="fw-bold text-muted border-bottom pb-1 mb-3 mt-3">
                  <i className="fas fa-briefcase me-2"></i>Dados profissionais
                </h6>
                <div className="row">
                  <Campo rotulo="Cargo" valor={colaboradorDetalhe.cargo} icone="fa-briefcase" />
                  <Campo rotulo="Departamento" valor={colaboradorDetalhe.departamento} icone="fa-sitemap" />
                  <Campo rotulo="Time" valor={colaboradorDetalhe.time} icone="fa-people-group" />
                  <Campo rotulo="Centro de Custo" valor={colaboradorDetalhe.centro_custo} icone="fa-coins" />
                  <Campo rotulo="Supervisor" valor={colaboradorDetalhe.supervisor} icone="fa-user-tie" />
                  <Campo rotulo="Matrícula" valor={colaboradorDetalhe.matricula} icone="fa-hashtag" />
                  <Campo rotulo="Salário" valor={formatarMoeda(colaboradorDetalhe.salario)} icone="fa-money-bill-wave" />
                </div>

                <h6 className="fw-bold text-muted border-bottom pb-1 mb-3 mt-3">
                  <i className="fas fa-user me-2"></i>Dados pessoais e contato
                </h6>
                <div className="row">
                  <Campo rotulo="CPF" valor={colaboradorDetalhe.cpf} icone="fa-id-card" />
                  <Campo rotulo="PIS" valor={colaboradorDetalhe.pis} icone="fa-id-badge" />
                  <Campo rotulo="RG" valor={colaboradorDetalhe.rg} icone="fa-address-card" />
                  <Campo rotulo="Nascimento" valor={colaboradorDetalhe.data_nascimento} icone="fa-cake-candles" />
                  <Campo rotulo="Gênero" valor={colaboradorDetalhe.genero} icone="fa-venus-mars" />
                  <Campo rotulo="Estado Civil" valor={colaboradorDetalhe.estado_civil} icone="fa-ring" />
                  <Campo rotulo="E-mail" valor={colaboradorDetalhe.email} icone="fa-envelope" />
                  <Campo rotulo="E-mail corporativo" valor={colaboradorDetalhe.email_corporativo} icone="fa-envelope-open" />
                  <Campo rotulo="Celular" valor={colaboradorDetalhe.celular} icone="fa-mobile-alt" />
                  <Campo rotulo="Telefone" valor={colaboradorDetalhe.telefone} icone="fa-phone" />
                  <Campo rotulo="CEP" valor={colaboradorDetalhe.cep} icone="fa-map-pin" />
                  <Campo rotulo="Cidade/UF" valor={[colaboradorDetalhe.cidade, colaboradorDetalhe.uf].filter(Boolean).join('/')} icone="fa-map-marker-alt" />
                </div>

                {/* ─── REGISTRO COMPLETO DA CONVENIA ─── */}
                <div className="mt-3">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary w-100"
                    onClick={() => setMostrarCompleto(v => !v)}
                  >
                    <i className={`fas ${mostrarCompleto ? 'fa-chevron-up' : 'fa-chevron-down'} me-2`}></i>
                    {mostrarCompleto ? 'Ocultar' : 'Mostrar'} todos os dados da Convenia
                    (desligamento, endereço, bancos, CTPS...)
                  </button>
                  {mostrarCompleto && (
                    <div className="border rounded p-3 mt-2 bg-light" style={{ maxHeight: '45vh', overflowY: 'auto' }}>
                      {valorVazio(colaboradorDetalhe.dados_completos) ? (
                        <span className="text-muted">Sem dados adicionais no espelho.</span>
                      ) : (
                        <RenderValor valor={colaboradorDetalhe.dados_completos} />
                      )}
                    </div>
                  )}
                </div>
                </>)}
              </div>

              <div className="modal-footer bg-light">
                {/* Exclusão em duas etapas: o clique arma a confirmação */}
                {confirmandoExclusao ? (
                  <div className="me-auto d-flex align-items-center gap-2 flex-wrap">
                    <span className="small text-danger fw-bold">
                      <i className="fas fa-triangle-exclamation me-1"></i>
                      Desfaz o desligamento no Saber (checklist + data). Na Convenia é manual.
                    </span>
                    <button type="button" className="btn btn-sm btn-danger"
                      onClick={excluirRescisao} disabled={excluindoRescisao}>
                      {excluindoRescisao
                        ? <span className="spinner-border spinner-border-sm"></span>
                        : <><i className="fas fa-trash me-1"></i>Confirmar exclusão</>}
                    </button>
                    <button type="button" className="btn btn-sm btn-secondary"
                      onClick={() => setConfirmandoExclusao(false)} disabled={excluindoRescisao}>
                      Cancelar
                    </button>
                  </div>
                ) : (
                  <button type="button" className="btn btn-sm btn-outline-danger me-auto"
                    onClick={() => setConfirmandoExclusao(true)}
                    title="Remove esta rescisão do banco do Saber — o colaborador volta a constar como ativo (não altera a Convenia)">
                    <i className="fas fa-trash me-2"></i>
                    Deletar rescisão
                  </button>
                )}
                <button type="button" className="btn btn-secondary" onClick={() => setColaboradorDetalhe(null)}>
                  <i className="fas fa-times me-2"></i>
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: CONFIRMAR DESLIGAMENTO NA CONVENIA (irreversível) */}
      {confirmandoConvenia && previaConvenia && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 10000, overflowY: 'auto' }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '700px' }}>
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-triangle-exclamation me-2 text-danger"></i>
                    Confirmar desligamento na Convenia
                  </h5>
                  <small style={{ color: '#6c757d' }}>
                    Ação irreversível — confira os dados antes de enviar
                  </small>
                </div>
                <button type="button" className="btn-close" disabled={processando}
                  onClick={() => { setConfirmandoConvenia(false); setPreviaConvenia(null); }}></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-danger">
                  <i className="fas fa-user-minus me-2"></i>
                  Vou <strong>desligar {previaConvenia.colaborador_convenia}</strong> na Convenia.
                  O desligamento <strong>não pode ser desfeito</strong> pela API.
                </div>

                {/* Campos editáveis — o que estiver aqui é o que vai para a Convenia */}
                {ajustes && (
                  <div className="row g-3 mb-3">
                    <div className="col-12">
                      <label className="form-label small fw-bold mb-1">Tipo de desligamento</label>
                      <select className="form-select" value={ajustes.tipo_desligamento}
                        onChange={setAj('tipo_desligamento')} disabled={processando}>
                        {[...new Set([
                          ...opcoesDesl.tipos_desligamento,
                          ...(ajustes.tipo_desligamento ? [ajustes.tipo_desligamento] : []),
                        ])].map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-bold mb-1">Aviso prévio</label>
                      <select className="form-select" value={ajustes.aviso_previo}
                        onChange={setAj('aviso_previo')} disabled={processando}>
                        {[...new Set([
                          ...opcoesDesl.avisos_previos,
                          ...(ajustes.aviso_previo ? [ajustes.aviso_previo] : []),
                        ])].map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label small fw-bold mb-1">Desvincular benefícios</label>
                      <select className="form-select" value={ajustes.desvincular_beneficios}
                        onChange={setAj('desvincular_beneficios')} disabled={processando}>
                        <option value="">Não informar</option>
                        <option value="Sim">Sim</option>
                        <option value="Não">Não</option>
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small fw-bold mb-1">Data do desligamento</label>
                      <input type="date" className="form-control" value={ajustes.data_final_desligamento}
                        onChange={setAj('data_final_desligamento')} disabled={processando} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small fw-bold mb-1">Início do aviso</label>
                      <input type="date" className="form-control" value={ajustes.data_inicio_aviso}
                        max={ajustes.data_final_desligamento || undefined}
                        onChange={setAj('data_inicio_aviso')} disabled={processando} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label small fw-bold mb-1">Remover acesso em</label>
                      <input type="date" className="form-control" value={ajustes.data_remocao_acesso}
                        onChange={setAj('data_remocao_acesso')} disabled={processando} />
                    </div>
                    <div className="col-12">
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" id="rs-quebra"
                          checked={ajustes.quebra_contrato} disabled={processando}
                          onChange={(e) => setAjustes(a => ({ ...a, quebra_contrato: e.target.checked }))} />
                        <label className="form-check-label" htmlFor="rs-quebra">
                          <strong>Quebra de contrato</strong>
                          <span className="text-muted"> — a Convenia calcula a multa</span>
                        </label>
                      </div>
                    </div>
                    <div className="col-12">
                      <label className="form-label small fw-bold mb-1">Motivo</label>
                      <textarea className="form-control" rows={2} value={ajustes.motivo_desligamento}
                        onChange={setAj('motivo_desligamento')} disabled={processando} />
                    </div>
                  </div>
                )}

                <div className="alert alert-success mb-0">
                  <i className="fas fa-shield-halved me-2"></i>
                  <strong>Não será enviado para a contabilidade.</strong> O endpoint de
                  desligamento da Convenia não tem campo de contabilidade, e nenhuma outra
                  rota é chamada — esse passo continua manual, na tela da Convenia.
                </div>
              </div>
              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-secondary" disabled={processando}
                  onClick={() => { setConfirmandoConvenia(false); setPreviaConvenia(null); }}>
                  <i className="fas fa-times me-2"></i>Cancelar
                </button>
                <button type="button" className="btn btn-danger" onClick={enviarParaConvenia}
                  disabled={processando}>
                  {processando
                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Enviando...</>
                    : <><i className="fas fa-paper-plane me-2"></i>Confirmar e desligar na Convenia</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DA SOLICITAÇÃO DE DESLIGAMENTO (formulário público) */}
      {/* ─── Modal: benefício pago × desligamento (VT, VA, Home Office) ─── */}
      {benefResc && (() => {
        const cfg = BENEF_RESC[benefResc.tipo];
        return (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10002 }}
          onClick={(e) => { if (e.target === e.currentTarget && !benefResc.anotando) setBenefResc(null); }}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content shadow-lg">
              <div className="modal-header bg-white">
                <div>
                  <h5 className="modal-title" style={{ color: '#212529' }}>
                    <i className={`fas ${cfg.icone} me-2`} style={{ color: '#00b5cc' }}></i>{cfg.titulo} na rescisão
                  </h5>
                  <div className="small" style={{ color: '#6c757d' }}>
                    {benefResc.dados?.colaborador || [colaboradorDetalhe?.nome, colaboradorDetalhe?.sobrenome].filter(Boolean).join(' ')}
                    {benefResc.dados?.data_desligamento && <> · desligamento <b>{benefResc.dados.data_desligamento}</b></>}
                    {benefResc.dados?.base_dias && <> · base {benefResc.dados.base_dias === 'corridos' ? 'dias corridos (mês = 30)' : 'dias úteis'}</>}
                  </div>
                </div>
                <button type="button" className="btn-close" disabled={benefResc.anotando} onClick={() => setBenefResc(null)}></button>
              </div>
              <div className="modal-body">
                {benefResc.carregando && <div className="text-center py-4"><span className="spinner-border text-primary"></span></div>}
                {benefResc.erro && <div className="alert alert-warning py-2 small"><i className="fas fa-triangle-exclamation me-2"></i>{benefResc.erro}</div>}
                {benefResc.dados && (() => {
                  const d = benefResc.dados;
                  const ROT = { ok: ['Trabalhou os dias pagos', 'success'], trabalhou_tudo: ['Saiu depois da competência', 'success'],
                    pago_apos: ['Pago além da saída', 'danger'], competencia_inteira_apos: ['Competência inteira após a saída', 'danger'],
                    sem_data: ['Sem data de desligamento', 'secondary'] };
                  const diasRot = d.base_dias === 'corridos' ? 'Dias' : 'Dias úteis';
                  return (
                    <>
                      <div className="d-flex flex-wrap gap-3 mb-3">
                        <div className="border rounded px-3 py-2" style={{ background: '#f8f9fa' }}>
                          <div className="small text-muted">Total pago</div>
                          <div className="fw-bold" style={{ fontSize: '1.2rem', fontVariantNumeric: 'tabular-nums' }}>{brlR(d.total_pago)}</div>
                        </div>
                        <div className={`border rounded px-3 py-2 ${d.total_descontar > 0 ? 'border-danger' : ''}`}
                          style={{ background: d.total_descontar > 0 ? '#fdeaec' : '#e6f7ef' }}>
                          <div className="small text-muted">Pago após o desligamento → descontar</div>
                          <div className={`fw-bold ${d.total_descontar > 0 ? 'text-danger' : 'text-success'}`}
                            style={{ fontSize: '1.2rem', fontVariantNumeric: 'tabular-nums' }}>{brlR(d.total_descontar)}</div>
                        </div>
                      </div>
                      {d.itens.length === 0 ? (
                        <div className="alert alert-secondary py-2 small mb-3"><i className="fas fa-circle-info me-2"></i>{d.resumo}</div>
                      ) : (
                        <div className="table-responsive mb-3">
                          <table className="table table-sm align-middle mb-0" style={{ fontSize: '.85rem' }}>
                            <thead className="table-light">
                              <tr><th>Competência</th><th>{benefResc.tipo === 'vt' ? 'Unidade · linha' : 'Benefício · vínculo'}</th><th className="text-end">{diasRot} pagos</th><th className="text-end">Valor pago</th>
                                <th className="text-end">Trab. até a saída</th><th className="text-end">Dias após</th><th className="text-end">Descontar</th><th>Situação</th></tr>
                            </thead>
                            <tbody>
                              {d.itens.map(x => {
                                const [rot, cor] = ROT[x.situacao] || [x.situacao, 'secondary'];
                                return (
                                  <tr key={x.id}>
                                    <td className="fw-semibold">{x.competencia}<div className="text-muted" style={{ fontSize: '.72rem' }}>{x.pago_em ? `pago ${x.pago_em}` : ''}</div></td>
                                    <td>{x.operadora || '—'}{x.linha ? <div className="text-muted" style={{ fontSize: '.72rem' }}>{x.linha}{x.modalidade === 'fixo' ? ' · valor fixo' : ''}</div> : (x.modalidade === 'fixo' ? <div className="text-muted" style={{ fontSize: '.72rem' }}>valor fixo</div> : null)}</td>
                                    <td className="text-end" style={{ fontVariantNumeric: 'tabular-nums' }}>{x.dias_pagos}<span className="text-muted">/{x.dias_uteis_competencia}</span></td>
                                    <td className="text-end" style={{ fontVariantNumeric: 'tabular-nums' }}>{brlR(x.valor_pago)}</td>
                                    <td className="text-end" style={{ fontVariantNumeric: 'tabular-nums' }}>{x.trabalhados_ate_saida ?? '—'}</td>
                                    <td className={`text-end ${x.dias_apos_desligamento ? 'text-danger fw-semibold' : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }}>{x.dias_apos_desligamento}</td>
                                    <td className={`text-end ${x.valor_descontar ? 'text-danger fw-bold' : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }}>{brlR(x.valor_descontar)}</td>
                                    <td><span className={`badge bg-${cor}`}>{rot}</span></td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                      <div className="border rounded p-2 small" style={{ background: '#f8f9fa' }}>
                        <div className="fw-semibold mb-1"><i className="fas fa-file-lines me-1 text-muted"></i>Resumo para a rescisão</div>
                        <div>{d.resumo}</div>
                        <div className="text-muted mt-1" style={{ fontSize: '.74rem' }}>
                          {benefResc.tipo === 'vt'
                            ? 'Passagem: valor/dia × dias úteis pagos depois da saída. Valor fixo (Mobilidade): proporcional aos dias úteis da competência.'
                            : d.base_dias === 'corridos'
                              ? 'Base em dias corridos (mês = 30, como Caju/VR contam): proporcional aos dias pagos depois da saída.'
                              : 'Base em dias úteis (com feriado do cliente): custo/dia × dias úteis pagos depois da saída.'}
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
              <div className="modal-footer bg-white">
                <button type="button" className="btn btn-secondary" disabled={benefResc.anotando} onClick={() => setBenefResc(null)}>Fechar</button>
                {benefResc.dados && (
                  <button type="button" className="btn btn-primary" disabled={benefResc.anotando} onClick={anotarBenefNaTarefa}
                    title={`Grava o resumo como observação da tarefa ${cfg.titulo}`}>
                    <i className={`fas ${benefResc.anotando ? 'fa-spinner fa-spin' : 'fa-comment-dots'} me-2`}></i>Anotar na tarefa
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        );
      })()}

      {/* ─── Modal: desligar do sistema de ponto (prévia → confirmar) ─── */}
      {desligPonto && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10002 }}
          onClick={(e) => { if (e.target === e.currentTarget && !desligPonto.executando) setDesligPonto(null); }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 680 }}>
            <div className="modal-content shadow-lg">
              <div className="modal-header bg-white">
                <div>
                  <h5 className="modal-title" style={{ color: '#212529' }}>
                    <i className="fas fa-user-slash me-2 text-danger"></i>Desligar do ponto
                  </h5>
                  <div className="small" style={{ color: '#6c757d' }}>
                    {[desligPonto.colab.nome, desligPonto.colab.sobrenome].filter(Boolean).join(' ')} · {empresaSelecionada?.cliente}
                  </div>
                </div>
                <button type="button" className="btn-close" disabled={desligPonto.executando} onClick={() => setDesligPonto(null)}></button>
              </div>
              <div className="modal-body">
                {sistemasPontoApi.length > 1 && (
                  <div className="mb-3">
                    <label className="form-label small fw-semibold mb-1">Sistema de ponto</label>
                    <select className="form-select form-select-sm" value={desligPonto.sistema} disabled={desligPonto.carregando || desligPonto.executando}
                      onChange={(e) => carregarPreviaDesligar(desligPonto.colab, e.target.value)}>
                      {sistemasPontoApi.map(x => <option key={x.tipo} value={x.tipo}>{x.rotulo}</option>)}
                    </select>
                  </div>
                )}
                {desligPonto.carregando && (
                  <div className="text-center py-4"><span className="spinner-border text-primary"></span>
                    <div className="text-muted small mt-2">Procurando o colaborador no sistema de ponto…</div></div>
                )}
                {!desligPonto.carregando && desligPonto.erro && (
                  <div className="alert alert-warning py-2 small mb-3">
                    <i className="fas fa-triangle-exclamation me-2"></i>{desligPonto.erro}
                    {desligPonto.naoAchado && portaisDaTarefa('ponto').length > 0 && (
                      <div className="mt-2">
                        {portaisDaTarefa('ponto').map(pt => (
                          <a key={pt.portal} href={pt.portal} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-secondary me-2">
                            <i className="fas fa-arrow-up-right-from-square me-1"></i>Abrir {pt.propriedade}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {!desligPonto.carregando && desligPonto.previa && (() => {
                  const p = desligPonto.previa;
                  const np = p.no_ponto || {};
                  return (
                    <>
                      <div className="row g-3">
                        <div className="col-md-6">
                          <div className="border rounded p-2 h-100" style={{ background: '#f8f9fa' }}>
                            <div className="small fw-semibold text-muted mb-1"><i className="fas fa-database me-1"></i>Na Convenia</div>
                            <div className="fw-semibold">{p.colaborador.nome}</div>
                            <div className="small text-muted">CPF {p.colaborador.cpf || '—'}{p.colaborador.matricula ? ` · matrícula ${p.colaborador.matricula}` : ''}</div>
                            <div className="small mt-1">Desligamento: <b>{p.colaborador.data_desligamento || '—'}</b></div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="border rounded p-2 h-100" style={{ background: '#f8f9fa' }}>
                            <div className="small fw-semibold text-muted mb-1"><i className="fas fa-clock me-1"></i>No {p.rotulo} (ao vivo)</div>
                            <div className="fw-semibold">{np.nome || '—'} <span className="text-muted small">#{np.id}</span></div>
                            <div className="small text-muted">{np.matricula ? `matrícula ${np.matricula}` : 'sem matrícula'}</div>
                            <div className="small mt-1">
                              {np.ativo
                                ? <span className="badge bg-success">ativo</span>
                                : <span className="badge bg-secondary">já desligado{np.desligado_em ? ` em ${np.desligado_em}` : ''}</span>}
                              {np.acesso_ativo !== undefined && np.ativo && (
                                <span className="badge bg-light text-dark border ms-1">acesso ao app {np.acesso_ativo ? 'ativo' : 'bloqueado'}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      {p.ja_desligado ? (
                        <div className="alert alert-secondary py-2 small mt-3 mb-0">
                          <i className="fas fa-circle-check me-2"></i>Já está desligado no {p.rotulo}. Confirmar só marca a tarefa como concluída.
                        </div>
                      ) : (
                        <>
                          <div className="mt-3">
                            <label className="form-label small fw-semibold mb-1">Data do desligamento no {p.rotulo}</label>
                            <input type="date" className="form-control form-control-sm" style={{ maxWidth: 200 }}
                              value={desligPonto.data} disabled={desligPonto.executando}
                              onChange={(e) => setDesligPonto(d => ({ ...d, data: e.target.value }))} />
                            <div className="form-text">Vem da Convenia; ajuste se o último dia trabalhado for outro.</div>
                          </div>
                          <div className="alert alert-danger py-2 small mt-3 mb-0">
                            <i className="fas fa-triangle-exclamation me-2"></i><b>O que vai acontecer:</b> {p.acao}
                            {' '}Nada é excluído — o histórico de batidas fica lá.
                          </div>
                        </>
                      )}
                    </>
                  );
                })()}
              </div>
              <div className="modal-footer bg-white">
                <button type="button" className="btn btn-secondary" disabled={desligPonto.executando} onClick={() => setDesligPonto(null)}>Cancelar</button>
                <button type="button" className="btn btn-danger" disabled={desligPonto.carregando || desligPonto.executando || !desligPonto.previa}
                  onClick={confirmarDesligarPonto}>
                  {desligPonto.executando
                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Desligando…</>
                    : desligPonto.previa?.ja_desligado
                      ? <><i className="fas fa-check me-2"></i>Marcar tarefa como concluída</>
                      : <><i className="fas fa-user-slash me-2"></i>Confirmar desligamento no {desligPonto.previa?.rotulo || 'ponto'}</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Modal: desligar na Convenia (checklist) ─── */}
      {desligConv && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10002 }}
          onClick={(e) => { if (e.target === e.currentTarget && !desligConv.executando) setDesligConv(null); }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 720 }}>
            <div className="modal-content shadow-lg">
              <div className="modal-header bg-white">
                <div>
                  <h5 className="modal-title" style={{ color: '#212529' }}>
                    <i className="fas fa-user-slash me-2 text-danger"></i>Desligar na Convenia
                  </h5>
                  <div className="small" style={{ color: '#6c757d' }}>
                    {[desligConv.colab.nome, desligConv.colab.sobrenome].filter(Boolean).join(' ')} · {empresaSelecionada?.cliente}
                  </div>
                </div>
                <button type="button" className="btn-close" disabled={desligConv.executando} onClick={() => setDesligConv(null)}></button>
              </div>
              <div className="modal-body">
                {desligConv.carregando && (
                  <div className="text-center py-4"><span className="spinner-border text-primary"></span>
                    <div className="text-muted small mt-2">Consultando a Convenia (situação, tipos de desligamento e aviso prévio)…</div></div>
                )}
                {!desligConv.carregando && desligConv.erro && (
                  <div className="alert alert-warning py-2 small mb-3"><i className="fas fa-triangle-exclamation me-2"></i>{desligConv.erro}</div>
                )}
                {!desligConv.carregando && desligConv.dados && (desligConv.dados.ja_desligado ? (
                  <div className="alert alert-secondary py-2 small mb-0">
                    <i className="fas fa-circle-check me-2"></i>{desligConv.dados.mensagem}
                    {desligConv.dados.status_convenia && <div className="mt-1 text-muted">Situação no espelho: {desligConv.dados.status_convenia}</div>}
                  </div>
                ) : desligConv.form && (
                  <>
                    {/* já saiu um POST de desligamento desta pessoa há pouco: o
                        desligamento é irreversível na Convenia, então aqui só se
                        confere lá antes de repetir */}
                    {desligConv.dados.ja_tentado && (
                      <div className="alert alert-danger py-2 small">
                        <i className="fas fa-circle-exclamation me-2"></i>
                        {desligConv.dados.ja_tentado_motivo}
                      </div>
                    )}
                    {desligConv.dados.data_obrigatoria && (
                      <div className="alert alert-warning py-2 small">
                        <i className="fas fa-calendar-day me-2"></i>
                        Não há data de desligamento no espelho da Convenia — informe a data abaixo.
                        Ela não é deduzida.
                      </div>
                    )}
                    <div className="row g-2">
                      <div className="col-md-6">
                        <label className="form-label small fw-semibold mb-1">Tipo de desligamento <span className="text-danger">*</span></label>
                        <select className="form-select form-select-sm" value={desligConv.form.tipo_desligamento}
                          onChange={(e) => setDesligConv((s) => ({ ...s, form: { ...s.form, tipo_desligamento: e.target.value,
                            quebra_contrato: /quebra/i.test(e.target.value) } }))}>
                          <option value="">— escolha —</option>
                          {(desligConv.dados.tipos || []).map((t) => <option key={t.id} value={t.nome}>{t.nome}</option>)}
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small fw-semibold mb-1">Aviso prévio <span className="text-danger">*</span></label>
                        <select className="form-select form-select-sm" value={desligConv.form.aviso_previo}
                          onChange={(e) => setDesligConv((s) => ({ ...s, form: { ...s.form, aviso_previo: e.target.value } }))}>
                          <option value="">— escolha —</option>
                          {(desligConv.dados.avisos || []).map((t) => <option key={t.id} value={t.nome}>{t.nome}</option>)}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label small fw-semibold mb-1">Data final do desligamento <span className="text-danger">*</span></label>
                        <input type="date" className="form-control form-control-sm" value={desligConv.form.data_final_desligamento}
                          onChange={(e) => setDesligConv((s) => ({ ...s, form: { ...s.form, data_final_desligamento: e.target.value } }))} />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label small fw-semibold mb-1">Início do aviso</label>
                        <input type="date" className="form-control form-control-sm" value={desligConv.form.data_inicio_aviso}
                          onChange={(e) => setDesligConv((s) => ({ ...s, form: { ...s.form, data_inicio_aviso: e.target.value } }))} />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label small fw-semibold mb-1">Remoção do acesso <span className="text-danger">*</span></label>
                        <input type="date" className="form-control form-control-sm" value={desligConv.form.data_remocao_acesso}
                          onChange={(e) => setDesligConv((s) => ({ ...s, form: { ...s.form, data_remocao_acesso: e.target.value } }))} />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label small fw-semibold mb-1">Desvincular benefícios</label>
                        <select className="form-select form-select-sm" value={desligConv.form.desvincular_beneficios}
                          onChange={(e) => setDesligConv((s) => ({ ...s, form: { ...s.form, desvincular_beneficios: e.target.value } }))}>
                          <option value="Sim">Sim</option><option value="Não">Não</option>
                        </select>
                      </div>
                      <div className="col-md-8 d-flex align-items-end">
                        <label className="form-check-label small d-inline-flex align-items-center gap-2" style={{ cursor: 'pointer' }}>
                          <input type="checkbox" className="form-check-input mt-0" checked={!!desligConv.form.quebra_contrato}
                            onChange={(e) => setDesligConv((s) => ({ ...s, form: { ...s.form, quebra_contrato: e.target.checked } }))} />
                          Quebra de contrato (a Convenia calcula a multa)
                        </label>
                      </div>
                      <div className="col-12">
                        <label className="form-label small fw-semibold mb-1">Motivo (opcional)</label>
                        <input type="text" className="form-control form-control-sm" maxLength={500} value={desligConv.form.motivo_desligamento}
                          onChange={(e) => setDesligConv((s) => ({ ...s, form: { ...s.form, motivo_desligamento: e.target.value } }))} />
                      </div>
                    </div>
                    <div className="alert alert-danger py-2 small mt-3 mb-0">
                      <i className="fas fa-triangle-exclamation me-2"></i><b>O que vai acontecer:</b> o desligamento é criado na Convenia
                      ({desligConv.dados.cliente}) e o colaborador passa a "Em desligamento". É <b>irreversível</b> por aqui.
                      Nada é enviado à contabilidade — esse passo continua manual na Convenia.
                    </div>
                  </>
                ))}
              </div>
              <div className="modal-footer bg-white">
                <button type="button" className="btn btn-secondary" disabled={desligConv.executando} onClick={() => setDesligConv(null)}>Cancelar</button>
                <button type="button" className={`btn ${desligConv.dados?.ja_desligado ? 'btn-success' : 'btn-danger'}`}
                  disabled={desligConv.carregando || desligConv.executando || !desligConv.dados
                    || (!desligConv.dados.ja_desligado && desligConv.dados.ja_tentado)
                    || (!desligConv.dados.ja_desligado && !(desligConv.form?.tipo_desligamento && desligConv.form?.aviso_previo && desligConv.form?.data_final_desligamento && desligConv.form?.data_remocao_acesso))}
                  onClick={confirmarDesligarConvenia}>
                  {desligConv.executando
                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Desligando…</>
                    : desligConv.dados?.ja_desligado
                      ? <><i className="fas fa-check me-2"></i>Marcar tarefa como concluída</>
                      : <><i className="fas fa-user-slash me-2"></i>Confirmar desligamento na Convenia</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Modal: solicitar exame demissional à clínica ─── */}
      {emailPrevia && (
        <EmailPreviaRescisao clienteId={emailPrevia.cliente_id} conveniaId={emailPrevia.convenia_id}
          colaborador={emailPrevia.nome} onFechar={() => setEmailPrevia(null)} onEnviado={concluirEmailPrevia} />
      )}
      {emailColab && (
        <EmailRescisaoColaborador clienteId={emailColab.cliente_id} conveniaId={emailColab.convenia_id}
          colaborador={emailColab.nome} onFechar={() => setEmailColab(null)} onEnviado={concluirEmailColab} />
      )}
      {cartaDesl && (
        <CartaDesligamento clienteId={cartaDesl.cliente_id} conveniaId={cartaDesl.convenia_id}
          colaborador={cartaDesl.nome} onFechar={() => setCartaDesl(null)} onEnviado={concluirCartaDesl} />
      )}
      {exameDem && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10002 }}
          onClick={(e) => { if (e.target === e.currentTarget) setExameDem(null); }}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content shadow-lg">
              <div className="modal-header bg-white">
                <div>
                  <h5 className="modal-title" style={{ color: '#212529' }}>
                    <i className="fas fa-stethoscope me-2" style={{ color: '#00b5cc' }}></i>Solicitar exame demissional
                  </h5>
                  <div className="small" style={{ color: '#6c757d' }}>
                    {[exameDem.colab.nome, exameDem.colab.sobrenome].filter(Boolean).join(' ')} · {empresaSelecionada?.cliente}
                    {exameDem.colab.data_desligamento && <> · desligamento {exameDem.colab.data_desligamento}</>}
                    {exameDem.config?.clinica_nome && <> · clínica <b>{exameDem.config.clinica_nome}</b></>}
                  </div>
                </div>
                <button type="button" className="btn-close" onClick={() => setExameDem(null)}></button>
              </div>
              <div className="modal-body">
                {!exameDem.config?.empresa && (
                  <div className="alert alert-warning py-2 small">
                    <i className="fas fa-triangle-exclamation me-2"></i>
                    Este cliente ainda não tem os <b>dados da guia</b> (empresa, CNPJ, unidade, clínica). Cadastre em{' '}
                    <Link to={`/Dp/Exame%20Mensal?cliente=${exameDem.cliente_id}`} target="_blank">Exame Mensal → Dados da guia</Link>{' '}
                    — a guia sai com o que houver.
                  </div>
                )}
                <div className="row g-3">
                  <div className="col-md-7">
                    <label className="form-label small fw-semibold mb-1">E-mail da clínica (destinatário)</label>
                    <div className="input-group input-group-sm">
                      <input type="text" className="form-control" placeholder="agendamento@clinica.com.br; coordenacao@..."
                        value={exameDem.destinatario}
                        onChange={(e) => setExameDem(x => ({ ...x, destinatario: e.target.value }))} />
                      {(exameDem.destinatario || '').trim() !== (exameDem.config?.clinica_email || '') && (
                        <button className="btn btn-outline-primary" type="button" disabled={exameDem.destSalvando}
                          onClick={salvarDestinatarioDem} title="Grava na configuração da guia deste cliente">
                          {exameDem.destSalvando ? <span className="spinner-border spinner-border-sm"></span> : 'Salvar'}
                        </button>
                      )}
                    </div>
                    {!exameDem.config?.clinica_email && (
                      <div className="form-text text-danger">Sem e-mail da clínica o envio é recusado — informe e salve.</div>
                    )}
                  </div>
                  <div className="col-md-5">
                    <label className="form-label small fw-semibold mb-1">Operador que assina</label>
                    <select className="form-select form-select-sm" value={exameDem.operador_id}
                      onChange={(e) => setExameDem(x => ({ ...x, operador_id: e.target.value }))}>
                      <option value="">Selecione…</option>
                      {exameDem.remetentes.map(r => <option key={r.id} value={r.id}>{r.nome}{r.email ? ` · ${r.email}` : ''}</option>)}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-semibold mb-1">Data do exame (opcional)</label>
                    <input type="date" className="form-control form-control-sm" value={exameDem.data}
                      onChange={(e) => setExameDem(x => ({ ...x, data: e.target.value }))} />
                  </div>
                  <div className="col-md-8">
                    <label className="form-label small fw-semibold mb-1">Assunto</label>
                    <input type="text" className="form-control form-control-sm" value={exameDem.assunto}
                      onChange={(e) => setExameDem(x => ({ ...x, assunto: e.target.value }))} />
                  </div>
                  <div className="col-12">
                    <label className="form-label small fw-semibold mb-1">Texto do e-mail</label>
                    <textarea className="form-control form-control-sm" rows={9} value={exameDem.corpo}
                      onChange={(e) => setExameDem(x => ({ ...x, corpo: e.target.value }))} />
                    <div className="form-text">A guia em PDF vai anexada; cópia ao grupo de e-mail do cliente, quando cadastrado.</div>
                  </div>
                </div>
                {/* a clínica não agenda com CPF/RG/nascimento/cargo em branco */}
                <ComplementoGuiaExame clienteId={exameDem.cliente_id} conveniaId={exameDem.colab.convenia_id}
                  faltando={exameDem.faltando}
                  onSalvo={() => abrirExameDem(exameDem.colab)} />
                {exameDem.historico.length > 0 && (
                  <div className="mt-3 small">
                    <div className="fw-semibold mb-1"><i className="fas fa-clock-rotate-left me-1 text-muted"></i>Pedidos anteriores</div>
                    {exameDem.historico.map((h, i) => (
                      <div key={i} className="text-muted">{h.em} · {h.tipo}{h.data_exame ? ` · exame ${h.data_exame}` : ''} · para {h.para}{h.operador ? ` · por ${h.operador}` : ''}</div>
                    ))}
                  </div>
                )}
              </div>
              <div className="modal-footer bg-white flex-wrap gap-2">
                <div className="me-auto d-flex gap-2 flex-wrap">
                  <button type="button" className="btn btn-light border" onClick={verGuiaDem}>
                    <i className="fas fa-file-pdf me-2 text-danger"></i>Ver guia (PDF)
                  </button>
                  <button type="button" className="btn btn-outline-primary" onClick={visualizarEmailDem}
                    title="O e-mail como vai chegar à clínica, com a guia anexada">
                    <i className="fas fa-eye me-2"></i>Visualizar e-mail
                  </button>
                </div>
                {exameDem.config?.clinica_portal && (
                  <>
                    <a className="btn btn-outline-secondary" href={exameDem.config.clinica_portal} target="_blank" rel="noreferrer"
                      title="Algumas clínicas só recebem o pedido pelo site">
                      <i className="fas fa-arrow-up-right-from-square me-2"></i>Portal da clínica
                    </a>
                    <button type="button" className="btn btn-outline-primary" disabled={exameDemEnviando} onClick={registrarExamePortalDem}
                      title="Fez o pedido no site da clínica? Registra aqui e marca a tarefa como Solicitado">
                      <i className="fas fa-check me-2"></i>Pedido feito pelo portal
                    </button>
                  </>
                )}
                <button type="button" className="btn btn-secondary" onClick={() => setExameDem(null)}>Cancelar</button>
                <button type="button" className="btn btn-primary" disabled={exameDemEnviando || !exameDem.operador_id || !exameDem.config?.clinica_email || (exameDem.faltando || []).length > 0}
                  onClick={visualizarEmailDem} title="Abre a prévia; o envio é confirmado lá">
                  <i className="fas fa-eye me-2"></i>Ver prévia e enviar
                </button>
              </div>
            </div>
          </div>
          {/* prévia por cima do modal do exame: o Enviar de verdade fica aqui */}
          {emailPreviewDem && (
            <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 10003 }}
              onClick={(e) => { if (e.target === e.currentTarget && !exameDemEnviando) fecharPreviewDem(); }}>
              <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content shadow-lg">
                  <div className="modal-header bg-white">
                    <h5 className="modal-title" style={{ color: '#212529' }}>
                      <i className="fas fa-envelope-open-text me-2" style={{ color: '#00b5cc' }}></i>Prévia do e-mail à clínica
                    </h5>
                    <button type="button" className="btn-close" disabled={exameDemEnviando} onClick={fecharPreviewDem}></button>
                  </div>
                  <div className="modal-body p-0">
                    {emailPreviewDem.carregando ? (
                      <div className="text-center py-5"><span className="spinner-border text-primary"></span>
                        <div className="text-muted small mt-2">Montando o e-mail e a guia…</div></div>
                    ) : (
                      <>
                        <div className="px-3 py-2 border-bottom small" style={{ background: '#f8f9fa', lineHeight: 1.7 }}>
                          <div><span className="text-muted" style={{ display: 'inline-block', width: 64 }}>De</span><b>{emailPreviewDem.de}</b></div>
                          <div><span className="text-muted" style={{ display: 'inline-block', width: 64 }}>Para</span><b>{emailPreviewDem.para}</b></div>
                          {emailPreviewDem.cc && <div><span className="text-muted" style={{ display: 'inline-block', width: 64 }}>Cc</span>{emailPreviewDem.cc}</div>}
                          {emailPreviewDem.bcc && <div><span className="text-muted" style={{ display: 'inline-block', width: 64 }}>Cco</span>{emailPreviewDem.bcc}</div>}
                          <div><span className="text-muted" style={{ display: 'inline-block', width: 64 }}>Assunto</span><b>{emailPreviewDem.assunto}</b></div>
                          <div><span className="text-muted" style={{ display: 'inline-block', width: 64 }}>Anexo</span><i className="fas fa-paperclip me-1"></i>{emailPreviewDem.anexo}</div>
                        </div>
                        <div className="row g-0">
                          <div className="col-lg-7 border-end">
                            <iframe title="E-mail" srcDoc={emailPreviewDem.html} sandbox="" style={{ width: '100%', height: '58vh', border: 0, background: '#fff' }} />
                          </div>
                          <div className="col-lg-5">
                            <iframe title="Guia em PDF" src={emailPreviewDem.pdfUrl} style={{ width: '100%', height: '58vh', border: 0 }} />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="modal-footer bg-white">
                    <button type="button" className="btn btn-secondary" disabled={exameDemEnviando} onClick={fecharPreviewDem}>
                      <i className="fas fa-arrow-left me-2"></i>Voltar e ajustar
                    </button>
                    <button type="button" className="btn btn-primary"
                      disabled={exameDemEnviando || emailPreviewDem.carregando || !exameDem.operador_id || !exameDem.config?.clinica_email || (exameDem.faltando || []).length > 0}
                      onClick={enviarExameDem}>
                      {exameDemEnviando ? <><span className="spinner-border spinner-border-sm me-2"></span>Enviando…</> : <><i className="fas fa-paper-plane me-2"></i>Enviar à clínica</>}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {solicitacaoDetalhe && (
        <div className="modal" tabIndex="-1"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999, overflowY: 'auto' }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '920px' }}>
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-user-minus me-2"></i>
                    Solicitação de desligamento — {solicitacaoDetalhe.colaborador}
                  </h5>
                  <small style={{ color: '#6c757d' }}>
                    {solicitacaoDetalhe.cliente || solicitacaoDetalhe.empresa}
                    {solicitacaoDetalhe.operador && ` · operador: ${solicitacaoDetalhe.operador}`}
                    {` · recebida em ${solicitacaoDetalhe.created_at}`}
                  </small>
                </div>
                <button type="button" className="btn-close" onClick={() => setSolicitacaoDetalhe(null)}
                  disabled={processando}></button>
              </div>

              <div className="modal-body">
                {/* veredito da checagem contra o espelho da Convenia */}
                {solicitacaoDetalhe.verificacao === 'encontrado' && solicitacaoDetalhe.colaborador_convenia && (
                  <div className="alert alert-success py-2 small">
                    <i className="fas fa-user-check me-2"></i>
                    Colaborador confirmado na Convenia:{' '}
                    <b>{solicitacaoDetalhe.colaborador_convenia.nome}</b>
                    {solicitacaoDetalhe.colaborador_convenia.cargo &&
                      <> · {solicitacaoDetalhe.colaborador_convenia.cargo}</>}
                    {solicitacaoDetalhe.colaborador_convenia.status &&
                      <> · {solicitacaoDetalhe.colaborador_convenia.status}</>}
                  </div>
                )}
                {solicitacaoDetalhe.verificacao === 'ja_desligado' && (
                  <div className="alert alert-warning py-2 small">
                    <i className="fas fa-user-clock me-2"></i>
                    <b>Atenção:</b> {solicitacaoDetalhe.colaborador_convenia?.nome || 'este colaborador'} já
                    consta como <b>desligado
                    {solicitacaoDetalhe.colaborador_convenia?.data_desligamento &&
                      ` em ${solicitacaoDetalhe.colaborador_convenia.data_desligamento}`}</b> na
                    Convenia — esta solicitação pode ser duplicada.
                  </div>
                )}
                {solicitacaoDetalhe.verificacao === 'nao_encontrado' && (
                  <div className="alert alert-danger py-2 small">
                    <i className="fas fa-user-xmark me-2"></i>
                    <b>Atenção:</b> nenhum colaborador
                    da {solicitacaoDetalhe.cliente || solicitacaoDetalhe.empresa} casa com
                    "<b>{solicitacaoDetalhe.colaborador}</b>" no espelho da Convenia.
                    Confira a grafia do nome com o solicitante antes de processar.
                  </div>
                )}
                {solicitacaoDetalhe.verificacao === 'ambiguo' && (
                  <div className="alert alert-warning py-2 small">
                    <i className="fas fa-people-arrows me-2"></i>
                    <b>Atenção:</b> mais de um colaborador da empresa casa com esse nome —
                    identifique a pessoa certa antes de processar.
                  </div>
                )}
                {solicitacaoDetalhe.verificacao === 'sem_cliente' && (
                  <div className="alert alert-danger py-2 small">
                    <i className="fas fa-building-circle-xmark me-2"></i>
                    <b>Atenção:</b> o CNPJ informado no formulário não casou com nenhum
                    cliente da carteira — cadastre o CNPJ em BPO RH → Clientes.
                  </div>
                )}
                {solForm && (<>
                <h6 className="fw-bold text-muted border-bottom pb-1 mb-3">
                  <i className="fas fa-file-signature me-2"></i>Desligamento
                  <span className="badge bg-light text-dark border ms-2 fw-normal">
                    edite o que precisar antes de processar
                  </span>
                </h6>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label small fw-bold mb-1">
                      Colaborador
                      {colabsAtivos.length > 0 && (
                        <span className="text-muted fw-normal ms-1">
                          ({colabsAtivos.length} ativos na empresa)
                        </span>
                      )}
                    </label>
                    {colabsAtivos.length > 0 ? (
                      <select className="form-select" value={solForm.colaborador}
                        onChange={setSol('colaborador')} disabled={salvandoSol || processando}>
                        {/* o nome digitado no formulário fica como 1ª opção quando
                            não casa com ninguém — trocar é decisão da operadora */}
                        {!colabsAtivos.some(c => c.nome === solForm.colaborador) && (
                          <option value={solForm.colaborador}>
                            {solForm.colaborador
                              ? `${solForm.colaborador} (como digitado — não está na Convenia)`
                              : 'Selecione o colaborador...'}
                          </option>
                        )}
                        {colabsAtivos.map(c => (
                          <option key={c.convenia_id} value={c.nome}>
                            {c.nome}{c.cargo ? ` — ${c.cargo}` : ''}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input type="text" className="form-control" value={solForm.colaborador}
                        onChange={setSol('colaborador')} disabled={salvandoSol || processando} />
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-bold mb-1">E-mail do solicitante</label>
                    <input type="email" className="form-control" value={solForm.email_solicitante}
                      onChange={setSol('email_solicitante')} disabled={salvandoSol || processando} />
                  </div>
                  <div className="col-md-8">
                    <label className="form-label small fw-bold mb-1">Tipo de desligamento</label>
                    <select className="form-select" value={solForm.tipo_desligamento}
                      onChange={setSol('tipo_desligamento')} disabled={salvandoSol || processando}>
                      <option value="">Selecione...</option>
                      {[...new Set([
                        ...opcoesDesl.tipos_desligamento,
                        ...(solForm.tipo_desligamento ? [solForm.tipo_desligamento] : []),
                      ])].map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-bold mb-1">Categoria</label>
                    <select className="form-select" value={solForm.categoria_desligamento}
                      onChange={setSol('categoria_desligamento')} disabled={salvandoSol || processando}>
                      <option value="">Sem categoria</option>
                      {opcoesDesl.categorias.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-bold mb-1">Aviso prévio</label>
                    <select className="form-select" value={solForm.aviso_previo}
                      onChange={setSol('aviso_previo')} disabled={salvandoSol || processando}>
                      <option value="">Selecione...</option>
                      {opcoesDesl.avisos_previos.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-bold mb-1">Início do aviso</label>
                    <input type="date" className="form-control" value={solForm.data_inicio_aviso}
                      onChange={setSol('data_inicio_aviso')} disabled={salvandoSol || processando} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-bold mb-1">Data final do desligamento</label>
                    <input type="date" className="form-control" value={solForm.data_final_desligamento}
                      min={solForm.data_inicio_aviso || undefined}
                      onChange={setSol('data_final_desligamento')} disabled={salvandoSol || processando} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-bold mb-1">Desvincular benefícios</label>
                    <select className="form-select" value={solForm.desvincular_beneficios}
                      onChange={setSol('desvincular_beneficios')} disabled={salvandoSol || processando}>
                      <option value="">-</option>
                      <option value="Sim">Sim</option>
                      <option value="Não">Não</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-bold mb-1">Remover acesso em</label>
                    <input type="date" className="form-control" value={solForm.data_remocao_acesso}
                      onChange={setSol('data_remocao_acesso')} disabled={salvandoSol || processando} />
                  </div>
                  <div className="col-12">
                    <label className="form-label small fw-bold mb-1">Motivo do desligamento</label>
                    <textarea className="form-control" rows={3} value={solForm.motivo_desligamento}
                      onChange={setSol('motivo_desligamento')} disabled={salvandoSol || processando} />
                  </div>
                </div>

                {!solicitacaoDetalhe.cliente && (
                  <div className="alert alert-warning mt-3 mb-0">
                    <i className="fas fa-triangle-exclamation me-2"></i>
                    O nome da empresa informado (<strong>{solicitacaoDetalhe.empresa}</strong>) não casou
                    com nenhum cliente da carteira — confirme de quem é esta solicitação antes de processar.
                  </div>
                )}
                </>)}
              </div>

              <div className="modal-footer bg-light">
                {confirmandoExclusaoSol ? (
                  <div className="me-auto d-flex align-items-center gap-2 flex-wrap">
                    <span className="small text-danger fw-bold">
                      <i className="fas fa-triangle-exclamation me-1"></i>
                      Excluir esta solicitação em definitivo?
                    </span>
                    <button type="button" className="btn btn-sm btn-danger"
                      onClick={excluirSolicitacao} disabled={processando || salvandoSol}>
                      {processando
                        ? <span className="spinner-border spinner-border-sm"></span>
                        : <><i className="fas fa-trash me-1"></i>Confirmar exclusão</>}
                    </button>
                    <button type="button" className="btn btn-sm btn-secondary"
                      onClick={() => setConfirmandoExclusaoSol(false)} disabled={processando}>
                      Cancelar
                    </button>
                  </div>
                ) : (
                  <button type="button" className="btn btn-sm btn-outline-danger me-auto"
                    onClick={() => setConfirmandoExclusaoSol(true)} disabled={processando || salvandoSol}
                    title="Exclui a solicitação (ex.: envio de teste ou duplicado)">
                    <i className="fas fa-trash me-2"></i>
                    Excluir
                  </button>
                )}
                <button type="button" className="btn btn-secondary" onClick={() => setSolicitacaoDetalhe(null)}
                  disabled={processando || salvandoSol}>
                  <i className="fas fa-times me-2"></i>
                  Fechar
                </button>
                <button type="button" className="btn btn-primary" onClick={salvarSolicitacao}
                  disabled={processando || salvandoSol}>
                  {salvandoSol
                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Salvando...</>
                    : <><i className="fas fa-save me-2"></i>Salvar alterações</>}
                </button>
                <button type="button" className="btn btn-success" onClick={verPrevia}
                  disabled={processando || salvandoSol}
                  title="Cria o desligamento na Convenia a partir destes dados">
                  {processando
                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Aguarde...</>
                    : <><i className="fas fa-paper-plane me-2"></i>Mandar para Convenia</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default BpoRhRescisoes;
